import { IntroNumberProvenance, SectionProvenance } from './number-provenance';
import { useEffect, useMemo, useRef, useState, type CSSProperties, type FormEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import {
  ArrowLeft, ArrowRight, Briefcase, CalendarBlank, CaretRight, ChatCircleDots, Check,
  Compass, DownloadSimple, FileText, Heart, House, Info, LockSimple, MagicWand,
  MoonStars, PaperPlaneRight, PencilSimple, ShieldCheck, Sparkle, StarFour, Sun, Trash,
  UserCircle, Warning, Wallet, X,
} from '@phosphor-icons/react';
import { Link, Navigate, NavLink, Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, idempotencyKey } from './api';
import { trackFunnel, trackCheckoutError, trackFunnelOnce, type ContinuationSurface, type EntitlementAnalyticsState } from './analytics';
import { introContinuationQuestions } from './intro-bridge';
import { clearPendingContinuation, rememberPendingContinuation } from './pending-continuation';
import { chatOpenedEventFor, explainerAnalytics, explainerContinuationState, explainerMatchesDisplayed, type NumbersExplainer } from './numbers-explainer-model';
import { NumbersExplainerSheet } from './numbers-explainer-sheet';
import { rememberLifecycleAttribution } from './lifecycle-attribution';
import { ErrorNotice } from './error-notice';
import { CelestialBackdrop, OnboardingStepper, IntroParagraphs, ReadingGeneration, RequestChoiceIcon } from './onboarding-design';
import { prepareOnboardingScene } from './onboarding-assets';
import {
  AuthConflictPage, CheckoutPage, entitlementStatusLabel, isReportProductId, PaymentReturnPage, PaywallSheet,
  ProductOffers, PurchasesSection, ReportCheckoutStub, type EntitlementBalance,
} from './commerce';
import type {
  CalendarDay, Forecast, IntentId, InterestId, Message, Onboarding, PremiumReportContent, PremiumReportDetail,
  PremiumReportListItem, Product, Reading, ReportPrepareResult, User,
} from './types';
import { formatReportDateRu, localizeMachineLabel, resolveComposerOrigin, splitNarrativeParagraphs, strongDateCaption } from './report-display';
import {
  authenticateTelegramMiniApp,
  getTelegramAuthStatus,
  getTelegramStartParam,
  isTelegramAuthPending,
  isTelegramMiniApp,
  looksLikeTelegramMiniAppShell,
  subscribeTelegramAuthStatus,
  type TelegramAuthStatus,
} from './telegram';

const PROFILE_STALE_MS = 300_000;
const NUMEROLOGY_STALE_MS = 300_000;
const REPORTS_LIST_STALE_MS = 60_000;
import {
  DAILY_CHECKING, DAILY_ERROR,
  PROFILE_LOADING, REPORT_LOADING_BY_PRODUCT, REPORT_LOADING_PHRASES,
} from './loading-copy';
import {
  checkExistingDailyForecast, DAILY_GENERATION_QUERY, DAILY_GENERATION_ROOT_KEY, deriveDailyForecastPhase,
  generateDailyForecast, generationStartedAt, requestSingleRetry, resetGenerationStart, type DailyForecastPhase,
} from './daily-forecast-flow';
import { DailyForecastGenerationProgress, type OrbitNumber } from './daily-forecast-generation';

const INTENTS: { id: IntentId; title: string; hint: string; icon: ReactNode }[] = [
  { id: 'relationships', title: 'Отношения', hint: 'Близость, границы, важный разговор', icon: <Heart /> },
  { id: 'money', title: 'Деньги', hint: 'Ресурсы, решения, привычки', icon: <Wallet /> },
  { id: 'career', title: 'Работа и своё дело', hint: 'Направление и следующий шаг', icon: <Briefcase /> },
  { id: 'decision', title: 'Важное решение', hint: 'Разобраться между вариантами', icon: <Compass /> },
  { id: 'self', title: 'Хочу лучше понять себя', hint: 'Сильные стороны и повторяющиеся темы', icon: <Sparkle /> },
  { id: 'other', title: 'Другое', hint: 'Расскажу своими словами', icon: <StarFour /> },
];

const DETAIL_COPY: Record<IntentId, { title: string; placeholder: string; choices: string[] }> = {
  relationships: { title: 'Что хочется изменить в отношениях?', placeholder: 'Она меня не понимает, и мы постоянно ссоримся', choices: ['Часто ссоримся', 'Не хватает внимания', 'Трудно говорить друг с другом', 'Не могу отпустить бывшего', 'Хочу начать новые отношения', 'Другое'] },
  money: { title: 'Что сейчас не устраивает в деньгах?', placeholder: 'Работаю больше, но доход почти не растёт', choices: ['Не получается больше зарабатывать', 'Деньги быстро заканчиваются', 'Доход то есть, то нет', 'Боюсь поднять цену', 'Хочу начать своё дело', 'Другое'] },
  career: { title: 'Что хочется изменить в работе?', placeholder: 'Коротко опиши, что происходит', choices: ['Хочу сменить работу', 'Устал от того, чем занимаюсь', 'Не понимаю свои сильные стороны', 'Хочу своё дело', 'Не знаю, куда двигаться', 'Другое'] },
  decision: { title: 'Что тебе предстоит решить?', placeholder: 'Какие варианты сейчас рассматриваешь?', choices: ['Про отношения', 'Про работу', 'Про деньги', 'Про переезд', 'Другое'] },
  self: { title: 'Что хочется лучше понять в себе?', placeholder: 'Добавь короткий пример из жизни', choices: ['Свои сильные стороны', 'Почему часто меняю цели', 'Почему трудно решиться', 'Что сейчас для меня важно', 'Другое'] },
  other: { title: 'Расскажи, с чем хочешь разобраться', placeholder: 'Коротко опиши ситуацию своими словами', choices: ['Другое'] },
};

function todayInZone(timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date());
  const value = Object.fromEntries(parts.map(part => [part.type, part.value]));
  return value.year + '-' + value.month + '-' + value.day;
}

function addDays(value: string, amount: number): string {
  const date = new Date(value + 'T12:00:00Z'); date.setUTCDate(date.getUTCDate() + amount);
  return date.toISOString().slice(0, 10);
}

function humanDate(value: string): string {
  return new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(value + 'T12:00:00Z'));
}

function monthYearRu(value: string): string {
  return new Intl.DateTimeFormat('ru-RU', { month: 'long', year: 'numeric', timeZone: 'UTC' })
    .format(new Date(value + 'T12:00:00Z'))
    .replace(/\s*г\.$/i, '')
    .toLocaleUpperCase('ru-RU') + ' Г.';
}

type ForecastIconName = 'heart' | 'wallet' | 'briefcase' | 'sparkle' | 'star' | 'sun' | 'warning';

type TodayForecastView = {
  title: string;
  opening: string;
  cards: { label: string; body: string; icon: ForecastIconName }[];
  tomorrowTeaser: string | null;
  continuationQuestions?: { id: string; text: string; angleType: string; role?: string; priorityReason?: string; sourceRefs?: string[] }[];
};

const INTERESTS: { id: InterestId; title: string; hint: string; cardLabel: string; icon: ForecastIconName }[] = [
  { id: 'relationships', title: 'Отношения', hint: 'Близость, разговоры, границы', cardLabel: 'Отношения сегодня', icon: 'heart' },
  { id: 'money', title: 'Деньги', hint: 'Доход, траты, решения о ресурсах', cardLabel: 'Деньги сегодня', icon: 'wallet' },
  { id: 'career', title: 'Работа', hint: 'Задачи, роль, своё дело', cardLabel: 'Работа сегодня', icon: 'briefcase' },
  { id: 'self', title: 'Внутренний фокус', hint: 'Состояние, ритм, понимание себя', cardLabel: 'Внутренний фокус сегодня', icon: 'sparkle' },
];
const MAX_INTERESTS = 3;

function toInterest(intent: IntentId): InterestId {
  return intent === 'relationships' || intent === 'money' || intent === 'career' ? intent : 'self';
}

function userInterests(user: User | undefined): InterestId[] {
  if (!user) return [];
  return (user.activeInterests?.length ? user.activeInterests : [toInterest(user.primaryIntent)]).slice(0, MAX_INTERESTS);
}

function legacyLabelIcon(label: string): ForecastIconName {
  if (/деньг/i.test(label)) return 'wallet';
  if (/работ/i.test(label)) return 'briefcase';
  if (/отношен/i.test(label)) return 'heart';
  return 'sparkle';
}

/** Non-breaking space keeps the day number from wrapping onto its own line. */
function dayTitle(dayNumber: number | string): string {
  return `Сегодня у тебя день\u00a0${dayNumber}`;
}

function entitlementAnalyticsState(balance: EntitlementBalance | undefined): EntitlementAnalyticsState {
  if (balance?.freeQuestionsRemaining) return 'free_available';
  if (balance?.paidQuestionsRemaining || balance?.unlimitedActive) return 'paid_balance';
  return 'none';
}

function openContinuation(
  navigate: ReturnType<typeof useNavigate>,
  question: string,
  sourceSurface: ContinuationSurface,
  angleType: string | undefined,
  entitlementState: EntitlementAnalyticsState,
  questionRank: 'primary' | 'secondary' = 'secondary',
) {
  rememberPendingContinuation({ question, sourceSurface, angleType, questionRank });
  void trackFunnel(
    sourceSurface === 'daily_forecast' ? 'daily_continuation_clicked' : 'free_intro_continuation_clicked',
    { sourceSurface, angleType, entitlementState, questionRank },
  );
  navigate('/numerologist', { state: { question, autoSend: true, sourceSurface, angleType, questionRank } });
}

function ContinuationBridge({
  questions, testId, onSelect,
}: {
  questions: { id?: string; text: string; angleType?: string; role?: string }[];
  testId: string;
  onSelect: (question: { text: string; angleType?: string }, rank: 'primary' | 'secondary') => void;
}) {
  const primary = questions[0];
  const secondary = questions.slice(1, 3);
  if (!primary) return null;
  return <div className="continuation-bridge" data-testid={testId}>
    <button type="button" className="continuation-primary" onClick={() => onSelect(primary, 'primary')}>
      <span className="continuation-kicker">Я бы начала с этого</span>
      <strong>{primary.text}</strong>
    </button>
    {secondary.length > 0 && <>
      <p className="continuation-secondary-label">Ещё можно спросить</p>
      <div className="continuation-secondary">
        {secondary.map(question => <button key={question.id ?? question.text} type="button" onClick={() => onSelect(question, 'secondary')}>{question.text}</button>)}
      </div>
    </>}
  </div>;
}

function readingQuestions(result: Reading['result']): { text: string; angleType?: string; role?: string }[] {
  return introContinuationQuestions(result);
}

function forecastView(result: Forecast['result'], dayNumber: number | string): TodayForecastView {
  if ('focusCards' in result) {
    return {
      title: dayTitle(dayNumber),
      opening: result.opening,
      cards: [
        ...result.focusCards.map(card => {
          const interest = INTERESTS.find(item => item.id === card.interestId);
          return { label: interest?.cardLabel ?? 'Главное сегодня', body: card.text, icon: interest?.icon ?? 'star' };
        }),
        { label: 'Общий фон', body: result.background, icon: 'sun' as const },
        { label: 'Осторожнее', body: result.caution, icon: 'warning' as const },
      ],
      tomorrowTeaser: result.tomorrowTeaser,
      continuationQuestions: result.schemaVersion === '3.0.0' ? result.continuationQuestions : undefined,
    };
  }
  if ('opening' in result && 'primary' in result) {
    return {
      title: dayTitle(dayNumber),
      opening: result.opening,
      cards: [
        { label: result.primaryLabel, body: result.primary, icon: legacyLabelIcon(result.primaryLabel) },
        { label: 'Общий фон', body: result.background, icon: 'sun' },
        { label: 'Осторожнее', body: result.caution, icon: 'warning' },
      ],
      tomorrowTeaser: result.tomorrowTeaser,
    };
  }
  if ('opening' in result) {
    return {
      title: dayTitle(dayNumber),
      opening: result.opening,
      cards: [
        { label: 'Отношения сегодня', body: result.relationships, icon: 'heart' },
        { label: 'Общий фон', body: result.money, icon: 'sun' },
        { label: 'Осторожнее', body: result.caution, icon: 'warning' },
      ],
      tomorrowTeaser: null,
    };
  }
  if (!('opening' in result)) {
    return {
      title: dayTitle(dayNumber),
      opening: result.shortSummary,
      cards: [
        { label: 'Сегодня для тебя', body: result.focus, icon: 'heart' },
        { label: 'Общий фон', body: result.practicalAction, icon: 'sun' },
        { label: 'Осторожнее', body: result.caution, icon: 'warning' },
      ],
      tomorrowTeaser: null,
    };
  }
  throw new Error('Неподдерживаемый формат прогноза.');
}

function retryDailyForecast(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError && error.code === 'AI_PROVIDER_QUOTA_EXHAUSTED') return false;
  return failureCount < 1;
}

function BrandMark({ compact = false }: { compact?: boolean }) {
  return <div className={'brand-mark' + (compact ? ' compact' : '')} aria-label="Numerology AI">
    <span className="brand-orbit"><span /></span><span>Numerology AI</span>
  </div>;
}

function PageHeader({ eyebrow, title, children }: { eyebrow?: string; title: string; children?: ReactNode }) {
  return <header className="page-header">
    <div>{eyebrow && <p className="eyebrow">{eyebrow}</p>}<h1>{title}</h1></div>{children}
  </header>;
}

function LoadingCard({ label = 'Собираем данные' }: { label?: string }) {
  return <div className="loading-card" role="status"><span className="mini-orbit" /><span>{label}</span></div>;
}

function BottomNav() {
  const items = [
    { to: '/today', label: 'Сегодня', icon: House },
    { to: '/numerologist', label: 'Нумеролог', icon: ChatCircleDots },
    { to: '/reports', label: 'Разборы', icon: FileText },
    { to: '/profile', label: 'Профиль', icon: UserCircle },
  ];
  return <nav className="bottom-nav app-bottom-nav" aria-label="Главная навигация">
    {items.map(item => <NavLink key={item.to} to={item.to} className={({ isActive }) => isActive ? 'active' : ''}>
      {({ isActive }) => <><item.icon weight={isActive ? 'fill' : 'regular'} /><span>{item.label}</span></>}
    </NavLink>)}
  </nav>;
}

function AppShell({ children }: { children: ReactNode }) {
  const location = useLocation();
  const todayMode = location.pathname === '/today' || location.pathname === '/__preview/today';
  const numerologistMode = location.pathname === '/numerologist';
  const reportsMode = location.pathname.startsWith('/reports') || location.pathname === '/__preview/reports';
  const profileMode = location.pathname === '/profile' || location.pathname === '/__preview/profile';
  return <div className={'app-shell' + (todayMode ? ' today-shell' : '') + (numerologistMode ? ' numerologist-shell' : '') + (reportsMode ? ' reports-shell' : '') + (profileMode ? ' profile-shell' : '')}><main className="page-content">{children}</main><BottomNav /></div>;
}

function Protected({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const telegramAuth = useTelegramAuthStatus();
  const miniAppShell = looksLikeTelegramMiniAppShell() || isTelegramMiniApp();
  const waitingTelegram = miniAppShell && isTelegramAuthPending(telegramAuth);
  const [sessionRecovery, setSessionRecovery] = useState<'idle' | 'recovering' | 'failed'>('idle');
  const profile = useQuery({
    queryKey: ['profile'],
    enabled: !waitingTelegram,
    staleTime: PROFILE_STALE_MS,
    queryFn: () => api.get<{ user: User }>('/v1/profile'),
    retry: (count, error) => {
      if (error instanceof ApiError && (error.status === 401 || error.code === 'ONBOARDING_INCOMPLETE')) return false;
      return count < 1;
    },
  });

  useEffect(() => {
    if (!(profile.error instanceof ApiError) || profile.error.status !== 401) return;
    if (!miniAppShell || sessionRecovery !== 'idle') return;
    let cancelled = false;
    setSessionRecovery('recovering');
    void (async () => {
      const outcome = await authenticateTelegramMiniApp({
        force: true,
        post: (path, body) => api.post(path, body),
        createSession: async () => {
          await api.post(
            '/v1/onboarding/sessions',
            { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone },
            idempotencyKey('tg-session-recovery'),
          );
        },
      });
      if (cancelled) return;
      if (outcome === 'authenticated' || outcome === 'pending_onboarding') {
        setSessionRecovery('idle');
        void queryClient.invalidateQueries({ queryKey: ['profile'] });
        void queryClient.invalidateQueries({ queryKey: ['numerology'] });
        void queryClient.invalidateQueries({ queryKey: ['auth-me'] });
        return;
      }
      setSessionRecovery('failed');
    })();
    return () => {
      cancelled = true;
    };
  }, [profile.error, miniAppShell, sessionRecovery, queryClient]);

  // Cached profile: keep tab content visible while auth is briefly pending (should be rare after bootstrap).
  const cachedUser = profile.data?.user;
  if (waitingTelegram && !profile.data?.user) return <LoadingCard label="Подтверждаем Telegram..." />;
  if (sessionRecovery === 'recovering') return <LoadingCard label="Восстанавливаем сессию..." />;
  if (profile.isPending && !cachedUser) return <LoadingCard label="Открываем пространство" />;
  if (profile.error instanceof ApiError && profile.error.status === 401) {
    if (miniAppShell) {
      return <div className="center-stage">
        <LoadingCard label={sessionRecovery === 'failed' ? 'Не удалось восстановить сессию' : 'Восстанавливаем сессию...'} />
        <p className="purchase-caption">Если экран не обновится, закрой Mini App и открой снова через бота.</p>
        <a className="primary-button" href="https://t.me/numero_ai1_bot">Открыть @numero_ai1_bot</a>
      </div>;
    }
    return <Navigate to="/" replace />;
  }
  if (profile.error instanceof ApiError && profile.error.code === 'ONBOARDING_INCOMPLETE') {
    // Stale cookie after payment return / session detach — never trap the user on a dead error line.
    if (miniAppShell) {
      return <div className="center-stage">
        <LoadingCard label="Восстанавливаем аккаунт..." />
        <p className="purchase-caption">Если экран не обновится, закрой Mini App и открой снова через бота.</p>
        <a className="primary-button" href="https://t.me/numero_ai1_bot">Открыть @numero_ai1_bot</a>
      </div>;
    }
    return <div className="center-stage">
      <h1>Открой приложение в Telegram</h1>
      <p className="purchase-caption">Оплата и профиль привязаны к аккаунту Telegram. Продолжи в Mini App.</p>
      <a className="primary-button" href="https://t.me/numero_ai1_bot">Открыть Numerology AI</a>
    </div>;
  }
  if (profile.error && !cachedUser) return <ErrorNotice error={profile.error} />;
  return <>{children}</>;
}

function WelcomePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const status = useQuery({
    queryKey: ['onboarding'], queryFn: () => api.get<{ onboarding: Onboarding }>('/v1/onboarding'), retry: false,
  });
  useEffect(() => {
    if (status.data?.onboarding.status === 'complete') navigate('/today', { replace: true });
  }, [status.data, navigate]);
  const start = useMutation({
    mutationFn: async () => {
      if (status.data?.onboarding) return status.data.onboarding;
      const result = await api.post<{ onboarding: Onboarding }>('/v1/onboarding/sessions', { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }, idempotencyKey());
      return result.onboarding;
    },
    onSuccess: async onboarding => { await prepareOnboardingScene('interests'); queryClient.setQueryData(['onboarding'], { onboarding }); navigate('/onboarding'); },
  });
  return <div className="welcome-page celestial-welcome celestial-surface">
    <CelestialBackdrop scene="welcome" />
    <section className="welcome-copy">
      <p className="eyebrow">Первый разбор бесплатно</p>
      <h1>Разберись в том,<br />что сейчас<br /><span>волнует</span></h1>
      <p>Отношения, деньги или работа. Расскажи о своей ситуации и укажи дату рождения - нумеролог подготовит персональный разбор и подскажет, на что обратить внимание.</p>
    </section>
    <div className="welcome-art-space" aria-hidden="true"><span>Гармония<br />внутри<br />тебя</span><span>Больше<br />чем<br />числа</span></div>
    <footer className="welcome-actions">
      <button className="primary-button large" onClick={() => start.mutate()} disabled={start.isPending}>
        {start.isPending ? 'Открываем...' : 'Продолжить'} <ArrowRight />
      </button>
      <p><LockSimple /> Без карты и оплаты</p>
      <small className="service-boundary">Нумерологическая интерпретация для самопознания</small>
      <ErrorNotice error={start.error} />
    </footer>
  </div>;
}

const STEP_INDEX: Record<Onboarding['nextStep'], number> = { primaryIntent: 0, displayName: 1, birthDate: 2, intentDetail: 3, complete: 4 };

const MONTHS_RU = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'] as const;

/** Native selects — smooth inside Telegram iOS WebView (custom wheel was laggy). */
function DateOfBirthPicker({
  day, month, year, maxYear, onChange,
}: {
  day: number | null; month: number | null; year: number | null; maxYear: number;
  onChange: (next: { day: number | null; month: number | null; year: number | null }) => void;
}) {
  const daysInMonth = month !== null && year !== null
    ? new Date(Date.UTC(year, month, 0)).getUTCDate()
    : 31;
  const safeDay = day !== null ? Math.min(day, daysInMonth) : null;
  const confirmed = day !== null && month !== null && year !== null;

  useEffect(() => {
    if (day !== null && month !== null && year !== null && safeDay !== null && safeDay !== day) {
      onChange({ day: safeDay, month, year });
    }
  }, [safeDay, day, month, year, onChange]);

  const years = useMemo(() => {
    const list: number[] = [];
    for (let value = maxYear; value >= 1900; value -= 1) list.push(value);
    return list;
  }, [maxYear]);

  return <div className="dob-picker">
    <div className="dob-selects" role="group" aria-label="Дата рождения">
      <label className="dob-select">
        <span>День</span>
        <select
          className={day !== null ? '' : 'dob-placeholder'}
          value={day !== null ? safeDay ?? '' : ''}
          aria-label="День"
          onChange={event => onChange({ day: Number(event.target.value), month, year })}
        >
          {day === null && <option value="" disabled>День</option>}
          {Array.from({ length: daysInMonth }, (_, index) => index + 1).map(value => (
            <option key={value} value={value}>{value}</option>
          ))}
        </select>
      </label>
      <label className="dob-select">
        <span>Месяц</span>
        <select
          className={month !== null ? '' : 'dob-placeholder'}
          value={month !== null ? month : ''}
          aria-label="Месяц"
          onChange={event => onChange({ day, month: Number(event.target.value), year })}
        >
          {month === null && <option value="" disabled>Месяц</option>}
          {MONTHS_RU.map((label, index) => (
            <option key={label} value={index + 1}>{label}</option>
          ))}
        </select>
      </label>
      <label className="dob-select">
        <span>Год</span>
        <select
          className={year !== null ? '' : 'dob-placeholder'}
          value={year !== null ? year : ''}
          aria-label="Год"
          onChange={event => onChange({ day, month, year: Number(event.target.value) })}
        >
          {year === null && <option value="" disabled>Год</option>}
          {years.map(value => (
            <option key={value} value={value}>{value}</option>
          ))}
        </select>
      </label>
    </div>
    <p className={'selected-date' + (confirmed ? '' : ' muted')}>
      {confirmed
        ? `Выбрано: ${safeDay} ${MONTHS_RU[month! - 1]} ${year}`
        : 'Выбери день, месяц и год'}
    </p>
  </div>;
}

function OnboardingPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const reduced = useReducedMotion();
  const query = useQuery({ queryKey: ['onboarding'], queryFn: () => api.get<{ onboarding: Onboarding }>('/v1/onboarding'), retry: false });
  const onboarding = query.data?.onboarding;
  const [localStep, setLocalStep] = useState<number | null>(null);
  const step = localStep ?? (onboarding ? STEP_INDEX[onboarding.nextStep] : 0);
  const [intent, setIntent] = useState<IntentId | null>(null);
  const [name, setName] = useState('');
  const currentYear = new Date().getFullYear();
  const [day, setDay] = useState<number | null>(null);
  const [month, setMonth] = useState<number | null>(null);
  const [year, setYear] = useState<number | null>(null);
  const dateConfirmed = day !== null && month !== null && year !== null;
  const [quickChoice, setQuickChoice] = useState('');
  const [detail, setDetail] = useState('');
  const intentSavingRef = useRef(false);
  const commitDate = useMemo(() => (next: { day: number | null; month: number | null; year: number | null }) => {
    setDay(next.day); setMonth(next.month); setYear(next.year);
  }, []);
  useEffect(() => {
    if (!onboarding) return;
    setIntent(current => current ?? onboarding.values.primaryIntent);
    setName(current => current || onboarding.values.displayName || '');
    if (onboarding.values.birthDate) {
      const [savedYear, savedMonth, savedDay] = onboarding.values.birthDate.split('-');
      setYear(Number(savedYear)); setMonth(Number(savedMonth)); setDay(Number(savedDay));
    }
    if (onboarding.values.intentDetail) {
      try { const saved = JSON.parse(onboarding.values.intentDetail); setQuickChoice(current => current || saved.quickChoice || ''); setDetail(current => current || saved.additionalText || ''); }
      catch { setDetail(current => current || onboarding.values.intentDetail || ''); }
    }
    if (onboarding.status === 'complete') navigate('/today', { replace: true });
  }, [onboarding, navigate]);
  const saveIntent = useMutation({
    mutationFn: (selected: IntentId) => api.put<{ onboarding: Onboarding }>('/v1/onboarding/steps/primary-intent', { primaryIntent: selected }),
    onSuccess: async data => { await prepareOnboardingScene('name'); queryClient.setQueryData(['onboarding'], data); setLocalStep(1); },
    onSettled: () => { intentSavingRef.current = false; },
  });
  const save = useMutation({
    mutationFn: async () => {
      if (step === 0) throw new Error('Выбери тему одним нажатием.');
      if (step === 1) { if (!name.trim()) throw new Error('Напиши, как к тебе обращаться.'); return api.put<{ onboarding: Onboarding }>('/v1/onboarding/steps/display-name', { displayName: name }); }
      if (step === 2) {
        if (!dateConfirmed || day === null || month === null || year === null) throw new Error('Выбери дату рождения.');
        const birthDate = String(year).padStart(4, '0') + '-' + String(month).padStart(2, '0') + '-' + String(day).padStart(2, '0');
        const parsed = new Date(birthDate + 'T12:00:00Z');
        if (Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== birthDate) throw new Error('Проверь дату рождения.');
        const age = new Date().getUTCFullYear() - year;
        if (age < 18) throw new Error('Сервис рассчитан на пользователей старше 18 лет.');
        if (parsed.getTime() > Date.now()) throw new Error('Дата рождения не может быть в будущем.');
        return api.put<{ onboarding: Onboarding }>('/v1/onboarding/steps/birth-date', { birthDate });
      }
      if (!quickChoice && !detail.trim()) throw new Error('Выбери вариант или опиши ситуацию своими словами.');
      const selected = quickChoice || 'Другое';
      const saved = await api.put<{ onboarding: Onboarding }>('/v1/onboarding/steps/intent-detail', { quickChoice: selected, additionalText: detail });
      await api.post('/v1/onboarding/complete', {});
      return saved;
    },
    onSuccess: async () => {
      await prepareOnboardingScene(['name', 'birth', 'request', 'generation'][step] ?? 'generation');
      queryClient.invalidateQueries({ queryKey: ['onboarding'] });
      if (step === 3) { queryClient.invalidateQueries({ queryKey: ['profile'] }); navigate('/reading'); }
      else setLocalStep(step + 1);
    },
  });
  if (query.isPending) return <div className="center-stage"><LoadingCard /></div>;
  if (query.error instanceof ApiError && query.error.status === 401) return <Navigate to="/" replace />;
  if (!onboarding) return <div className="center-stage"><ErrorNotice error={query.error} /></div>;
  const titles = ['С чем хочешь разобраться?', 'Как к тебе обращаться?', 'Твоя дата рождения', DETAIL_COPY[intent ?? 'other'].title];
  const subtitles = ['Сначала выберем тему твоего бесплатного разбора.', 'Познакомимся перед твоим разбором.', 'Использую её для персонального нумерологического разбора.', 'Выбери готовый вариант или расскажи своими словами.'];
  return <div className={`onboarding-page celestial-onboarding celestial-surface step-${step}`} data-intent={intent ?? 'other'}>
    <CelestialBackdrop scene={['interests', 'name', 'birth', 'request'][step] ?? 'interests'} />
    <header className="onboarding-header">
      <button className="icon-button" onClick={() => step > 0 ? setLocalStep(step - 1) : navigate('/')} disabled={save.isPending || saveIntent.isPending} aria-label="Назад"><ArrowLeft /></button>
      <OnboardingStepper step={step} />
    </header>
    <AnimatePresence mode="wait">
      <motion.section key={step} className="onboarding-step" initial={reduced ? false : { opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={reduced ? undefined : { opacity: 0, x: -20 }}>
        <p className="eyebrow">Настройка профиля</p><h1>{titles[step]}</h1><p className="step-subtitle">{subtitles[step]}</p>
        {step === 0 && <div className="intent-grid">{INTENTS.map(item => <button key={item.id} data-intent={item.id} disabled={saveIntent.isPending} className={'intent-card' + (intent === item.id ? ' selected' : '')} onClick={() => { if (intentSavingRef.current) return; intentSavingRef.current = true; if (intent !== item.id) { setQuickChoice(''); setDetail(''); } setIntent(item.id); saveIntent.mutate(item.id); }}>
          <span>{item.icon}</span><strong>{item.title}</strong><small>{item.hint}</small>
        </button>)}</div>}
        {step === 1 && <div className="name-field"><label className="field-block"><span>Имя</span><input value={name} onChange={event => { setName(event.target.value); if (save.error) save.reset(); }} maxLength={80} placeholder="Например, Анна" autoComplete="name" /></label>{name && <button type="button" className="clear-name" aria-label="Очистить имя" onClick={() => setName('')}><X /></button>}</div>}
        {step === 2 && <DateOfBirthPicker day={day} month={month} year={year} maxYear={currentYear - 18} onChange={commitDate} />}
        {step === 3 && <div><div className="quick-choices">{DETAIL_COPY[intent ?? 'other'].choices.map((choice, index) => <button type="button" key={choice} aria-pressed={quickChoice === choice} className={quickChoice === choice ? 'selected' : ''} onClick={() => setQuickChoice(choice)}><RequestChoiceIcon intent={intent ?? 'other'} index={index} other={choice === 'Другое'} /><span>{choice}</span></button>)}</div><label className="field-block compact-text"><span>Можешь добавить пару слов</span><textarea value={detail} onChange={event => setDetail(event.target.value)} maxLength={700} placeholder={DETAIL_COPY[intent ?? 'other'].placeholder} /><small>{detail.length}/700</small></label></div>}
        <ErrorNotice error={saveIntent.error ?? save.error} />
      </motion.section>
    </AnimatePresence>
    {step > 0 && <div className="sticky-action"><button className="primary-button large" onClick={() => save.mutate()} disabled={save.isPending || (step === 2 && !dateConfirmed)}>
      {save.isPending ? 'Сохраняем...' : step === 3 ? 'Получить бесплатный разбор' : 'Продолжить'} <ArrowRight />
    </button></div>}
  </div>;
}

function ReadingPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [elapsed, setElapsed] = useState(0);
  const status = useQuery({
    queryKey: ['reading-status'],
    queryFn: () => api.get<{ introductoryReading: { available: boolean; latest: Reading | null } }>('/v1/ai/introductory-reading/status'),
  });
  const generate = useMutation({
    mutationFn: () => api.post<{ reading: Reading }>('/v1/ai/introductory-reading', {}, idempotencyKey('reading')),
    onSuccess: data => { queryClient.setQueryData(['reading-status'], { introductoryReading: { available: true, latest: data.reading } }); },
  });
  useEffect(() => {
    if (status.data && !status.data.introductoryReading.latest && !generate.isPending && !generate.data && !generate.error) generate.mutate();
  }, [status.data, generate.isPending, generate.data, generate.error]);
  useEffect(() => {
    if (!generate.isPending) return;
    const started = Date.now();
    const timer = window.setInterval(() => setElapsed(Math.floor((Date.now() - started) / 1000)), 1000);
    return () => window.clearInterval(timer);
  }, [generate.isPending]);
  const currentSnapshot = status.data?.introductoryReading.latest ?? null;
  const reading = generate.data?.reading ?? currentSnapshot;
  const entitlement = useQuery({
    queryKey: ['entitlements'],
    queryFn: () => api.get<{ balance: EntitlementBalance }>('/v1/ai/entitlements'),
  });
  const freeQuestionAvailable = (entitlement.data?.balance.freeQuestionsRemaining ?? 0) > 0;
  useEffect(() => {
    if (!reading) return;
    trackFunnelOnce(`intro-view:${reading.readingId}`, 'free_intro_viewed');
    if(reading.numberProvenance?.facts.length) trackFunnelOnce(`intro-numbers:${reading.readingId}`, 'intro_number_provenance_impression');
    const bridge = readingQuestions(reading.result);
    if (bridge.length) trackFunnelOnce(`intro-bridge:${reading.readingId}`, 'free_intro_continuation_impression', {
      sourceSurface: 'introductory_reading', angleType: bridge[0]?.angleType, entitlementState: entitlementAnalyticsState(entitlement.data?.balance),
    });
  }, [reading, entitlement.data?.balance]);
  if (status.isPending || (generate.isPending && !reading)) return <ReadingGeneration elapsed={elapsed} />;
  if (generate.error || status.error) return <div className="generation-page celestial-generation celestial-surface generation-error"><CelestialBackdrop scene="generation" /><div className="error-symbol"><MagicWand /></div><h1>Разбор пока не сложился</h1><p>Дата и числа сохранены. Текст можно запросить ещё раз - без потери профиля.</p><ErrorNotice error={generate.error ?? status.error} /><button className="primary-button" onClick={() => { setElapsed(0); generate.mutate(); }}>Повторить разбор</button></div>;
  if (!reading) return <div className="center-stage"><LoadingCard /></div>;
  const v3 = reading.result.schemaVersion === '3.0.0' ? reading.result : null;
  const v2 = reading.result.schemaVersion === '2.0.0' ? reading.result : null;
  const legacy = 'sections' in reading.result ? reading.result : null;
  const questions = readingQuestions(reading.result);
  const entitlementState = entitlementAnalyticsState(entitlement.data?.balance);
  const heroNumber = reading.result.usedCalculationRefs.find(ref => ref.id === 'classical.lifePath')?.value;
  return <div className="reading-page celestial-reading celestial-surface">
    <CelestialBackdrop scene="reading" />
    <header className="reading-hero">
      <p className="eyebrow">Вводный разбор</p>
      <h1>{v3?.headline ?? v2?.headline ?? (legacy && 'title' in legacy ? legacy.title : 'Твой разбор')}</h1>
      <IntroParagraphs className="reading-lede" text={v3?.opening ?? v2?.opening ?? (legacy && 'intro' in legacy ? legacy.intro : '')} />
      <div className="reading-seal"><span className="num-center">{heroNumber}</span><small>число пути</small></div>
    </header>
    {reading.stale && <div className="notice stale-reading"><Info /><span>Этот разбор создан до изменения профиля.</span><button onClick={() => generate.mutate()} disabled={generate.isPending}>Обновить разбор</button></div>}
    <IntroNumberProvenance provenance={reading.numberProvenance} />
    {v3 ? <>
      <section className="reading-card"><h2>Главное в числах</h2><IntroParagraphs text={v3.coreInsight} /><SectionProvenance path="coreInsight" provenance={reading.numberProvenance} sections={reading.sectionProvenance} /></section>
      <section className="reading-card"><h2>Что меняется сейчас</h2><IntroParagraphs text={v3.currentPeriod} /><SectionProvenance path="currentPeriod" provenance={reading.numberProvenance} sections={reading.sectionProvenance} /></section>
      {v3.timeline.length > 0 && <section className="focus-card period-card"><p className="eyebrow">Ближайшие месяцы</p><div className="timeline-list">{v3.timeline.map((item,index) => <div key={item.period}><span className="num-center">{item.number}</span><div><strong>{item.period}</strong><IntroParagraphs text={item.text} /><SectionProvenance path={`timeline.${index}.text`} provenance={reading.numberProvenance} sections={reading.sectionProvenance} /></div></div>)}</div></section>}
      <section className="reading-card"><IntroParagraphs text={v3.closing} /><SectionProvenance path="closing" provenance={reading.numberProvenance} sections={reading.sectionProvenance} /></section>
    </> : v2 ? <>
      <section className="reading-sections">{v2.insights.map((insight, index) => <article key={insight.title} className="reading-card"><span className="section-number">{String(index + 1).padStart(2, '0')}</span><h2>{insight.title}</h2><IntroParagraphs text={insight.body} /></article>)}</section>
      <section className="focus-card period-card"><p className="eyebrow">Ближайшие месяцы</p><h2>Как меняется период</h2><div className="timeline-list">{v2.timeline.map(item => <div key={item.period}><span className="num-center">{item.number}</span><div><strong>{item.period}</strong><IntroParagraphs text={item.text} /></div></div>)}</div><IntroParagraphs className="hope-copy" text={v2.hope} /></section>
      <section className="reading-card open-loop"><p className="eyebrow">Куда посмотреть глубже</p><IntroParagraphs className="open-loop-body" text={v2.openLoop} /></section>
    </> : <>
      <section className="reading-sections">{legacy!.sections.map((section, index) => <article key={section.id} className="reading-card"><span className="section-number">{String(index + 1).padStart(2, '0')}</span><h2>{section.title}</h2><IntroParagraphs text={section.body} /></article>)}</section>
      <section className="focus-card"><p className="eyebrow">С чего начать</p><h2>Один шаг</h2><ol>{legacy!.practicalFocus.map(item => <li key={item}>{item}</li>)}</ol></section>
    </>}
    <ContinuationBridge questions={questions} testId="intro-continuation" onSelect={(question, rank) => { if(reading.numberProvenance?.facts.length && rank==='primary') void trackFunnel('intro_number_provenance_continue'); openContinuation(navigate, question.text, 'introductory_reading', question.angleType, entitlementState, rank); }} />
    {freeQuestionAvailable && questions[0] && <p className="continuation-free-note">Первый вопрос бесплатный. Нажми главную карточку.</p>}
    <button className="text-button" onClick={() => navigate('/today')}>Посмотреть подсказку на сегодня</button>
  </div>;
}

function CelestialNumberArt({ value }: { value: number | string }) {
  return <div className="celestial-number" data-day-number={String(value)} aria-label={`Число дня ${value}`}>
    <span className="celestial-constellation" aria-hidden="true" />
    <span className="celestial-axis" aria-hidden="true" />
    <span className="celestial-ring ring-a" aria-hidden="true" />
    <span className="celestial-ring ring-b" aria-hidden="true" />
    <span className="celestial-ring ring-c" aria-hidden="true" />
    <span className="celestial-ring ring-d" aria-hidden="true" />
    <span className="celestial-node node-a" aria-hidden="true" />
    <span className="celestial-node node-b" aria-hidden="true" />
    <span className="celestial-node node-c" aria-hidden="true" />
    <StarFour className="celestial-spark spark-a" weight="fill" aria-hidden="true" />
    <StarFour className="celestial-spark spark-b" weight="fill" aria-hidden="true" />
    <StarFour className="celestial-spark spark-c" weight="fill" aria-hidden="true" />
    <strong className="celestial-value num-center" data-value={String(value)}>{value}</strong>
    <small className="celestial-words">Сила<br />Баланс<br />Результат</small>
    <span className="celestial-infinity" aria-hidden="true">∞</span>
  </div>;
}

function ForecastIcon({ type }: { type: ForecastIconName }) {
  const icon = type === 'heart'
    ? <Heart className="medallion-main-icon" weight="duotone" />
    : type === 'wallet'
      ? <Wallet className="medallion-main-icon" weight="duotone" />
      : type === 'briefcase'
        ? <Briefcase className="medallion-main-icon" weight="duotone" />
        : type === 'sparkle'
          ? <Sparkle className="medallion-main-icon" weight="duotone" />
          : type === 'star'
            ? <StarFour className="medallion-main-icon" weight="duotone" />
            : type === 'sun'
              ? <Sun className="medallion-main-icon" weight="duotone" />
              : <Warning className="medallion-main-icon" weight="duotone" />;
  return <>{icon}<StarFour className="medallion-spark" weight="fill" /><span className="medallion-gem" /></>;
}

function InterestGlyph({ id }: { id: InterestId }) {
  if (id === 'relationships') return <Heart weight="duotone" />;
  if (id === 'money') return <Wallet weight="duotone" />;
  if (id === 'career') return <Briefcase weight="duotone" />;
  return <Sparkle weight="duotone" />;
}

function orbitNumbersFor(day: CalendarDay | undefined): OrbitNumber[] | undefined {
  if (!day) return undefined;
  return [
    { id: 'year', value: day.personalYear, label: 'год' },
    { id: 'month', value: day.personalMonth, label: 'месяц' },
    { id: 'day', value: day.personalDay, label: 'день' },
  ];
}

function TodayView({
  displayName,
  localDate,
  greeting,
  calendarDays,
  dayNumber,
  forecast,
  phase = 'ready',
  error,
  generation,
  revealAnimated = false,
  retrying = false,
  calendarError,
  onRetryCalendar,
  onRetryForecast,
  calendarRetryLabel = 'Повторить календарь',
}: {
  displayName: string;
  localDate: string;
  greeting: string;
  calendarDays?: CalendarDay[];
  dayNumber: number | string;
  forecast?: TodayForecastView;
  phase?: DailyForecastPhase;
  error?: unknown;
  generation?: { startedAt: number; onRevealReady: () => void };
  revealAnimated?: boolean;
  retrying?: boolean;
  calendarError?: unknown;
  onRetryCalendar?: () => void;
  onRetryForecast?: () => void;
  calendarRetryLabel?: string;
}) {
  const reduceMotion = useReducedMotion();
  const navigate = useNavigate();
  const entitlement = useQuery({
    queryKey: ['entitlements'],
    queryFn: () => api.get<{ balance: EntitlementBalance }>('/v1/ai/entitlements'),
  });
  const dailyEntitlement = entitlementAnalyticsState(entitlement.data?.balance);
  const showRitual = (phase === 'generating' || phase === 'completing') && generation;
  useEffect(() => {
    if (phase !== 'ready' || !forecast) return;
    trackFunnelOnce(`daily-view:${localDate}`, 'daily_forecast_viewed', { sourceSurface: 'daily_forecast' });
    if (forecast.continuationQuestions?.length) {
      trackFunnelOnce(`daily-bridge:${localDate}`, 'daily_continuation_impression', {
        sourceSurface: 'daily_forecast', angleType: forecast.continuationQuestions[0]?.angleType,
      });
    }
  }, [phase, forecast, localDate]);
  return <div className="today-page">
    <div className="today-stars" aria-hidden="true" />
    <header className="today-hero">
      <p className="today-month">{monthYearRu(localDate)}</p>
      <h1>{greeting}, <span>{displayName}</span></h1>
      <p className="today-manifesto">Каждый день - число, которое говорит с тобой</p>
      <div className="today-orbit-copy" aria-hidden="true"><i /><span>Числа<br />вдохновляют<br />понимать<br />себя</span></div>
    </header>

    <section className="day-strip today-day-strip" aria-label="Персональные дни" data-calendar-state={calendarDays ? 'ready' : calendarError ? 'error' : 'loading'}>
      {calendarDays ? calendarDays.map(day => {
        const current = day.date === localDate;
        const weekday = new Intl.DateTimeFormat('ru-RU', { weekday: 'short', timeZone: 'UTC' }).format(new Date(day.date + 'T12:00:00Z')).replace('.', '');
        return <div key={day.date} className={current ? 'current' : ''} aria-current={current ? 'date' : undefined}>
          <small>{weekday}</small><span>{Number(day.date.slice(-2))}</span><strong className="num-center">{day.personalDay}</strong>{current && <i aria-hidden="true" />}
        </div>;
      }) : Array.from({ length: 7 }, (_, index) => <div className="day-skeleton" key={index} aria-hidden="true"><small>·</small><span>·</span><strong>·</strong></div>)}
    </section>
    {Boolean(calendarError) && !calendarDays && onRetryCalendar && <div className="calendar-retry" role="alert"><p>Календарь персональных дней не загрузился.</p><button className="secondary-button" onClick={onRetryCalendar}>{calendarRetryLabel}</button></div>}

    <motion.section className={'today-forecast' + (phase !== 'ready' ? ' forecast-pending' : '')} data-forecast-phase={phase}
      initial={reduceMotion ? false : { opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
      <div className="forecast-cosmos" aria-hidden="true" />
      {showRitual ? <DailyForecastGenerationProgress
        key={generation.startedAt}
        startedAt={generation.startedAt}
        succeeded={phase === 'completing'}
        numbers={orbitNumbersFor(calendarDays?.find(day => day.date === localDate))}
        onRevealReady={generation.onRevealReady}
      /> : phase === 'checking' ? <div className="forecast-intro forecast-flow forecast-checking" role="status">
        <CelestialNumberArt value={dayNumber} />
        <p className="today-overline forecast-kicker">Прогноз дня</p>
        <p className="forecast-checking-label">{DAILY_CHECKING}</p>
        <span className="shimmer-line" /><span className="shimmer-line short" />
      </div> : phase === 'error' ? <div className="forecast-flow dfg-error" role="alert">
        <p className="today-overline forecast-kicker">Прогноз дня</p>
        <span className="dfg-error-mark" aria-hidden="true"><span /></span>
        <h2>{DAILY_ERROR.title}</h2>
        <p className="dfg-error-body">{DAILY_ERROR.body}</p>
        <button type="button" className="primary-button dfg-retry" onClick={onRetryForecast} disabled={retrying}>{DAILY_ERROR.retry}</button>
        <ErrorNotice error={error} />
      </div> : forecast && <motion.div className="forecast-reveal"
        initial={revealAnimated && !reduceMotion ? { opacity: 0, y: 8 } : false}
        animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, ease: 'easeOut' }}>
      <div className="forecast-intro forecast-flow">
        <CelestialNumberArt value={dayNumber} />
        <p className="today-overline forecast-kicker">Прогноз дня</p>
        <h2>{forecast.title}</h2>
        <p className="forecast-summary">{forecast.opening}</p>
      </div>
        <div className="today-forecast-points forecast-points">
          {forecast.cards.map(card => <article key={card.label}>
            <span className={`forecast-medallion ${card.icon}`} aria-hidden="true"><ForecastIcon type={card.icon} /></span>
            <div><span>{card.label}</span><p>{card.body}</p></div>
            <ArrowRight aria-hidden="true" />
          </article>)}
        </div>
        {forecast.tomorrowTeaser && <p className="today-tomorrow">{forecast.tomorrowTeaser}</p>}
        {Boolean(forecast.continuationQuestions?.length) && <ContinuationBridge
          questions={forecast.continuationQuestions!}
          testId="daily-continuation"
          onSelect={(question, rank) => openContinuation(navigate, question.text, 'daily_forecast', question.angleType, dailyEntitlement, rank)}
        />}
        <Link className={'today-cta' + (forecast.continuationQuestions?.length ? ' secondary' : '')} to="/numerologist"><StarFour weight="fill" /><strong>Спросить про сегодняшний день</strong><span><ArrowRight /></span></Link>
      </motion.div>}
    </motion.section>

    <div className="celestial-divider" aria-hidden="true"><span /><MoonStars /><span /></div>
    <section className="today-continue">
      <div className="continue-heading"><div><p className="today-overline">Продолжить</p><h2>Пространство для себя</h2></div><small>Больше<br />гармонии<br />каждый день</small></div>
      <div className="today-actions">
        <Link className="today-action-card question" to="/numerologist">
          <span className="action-medallion" aria-hidden="true"><ChatCircleDots weight="duotone" /><StarFour weight="fill" /></span>
          <img className="action-illustration action-orb-art" src="/assets/today/crystal-orb-v1.webp" width="640" height="640" decoding="async" alt="" aria-hidden="true" />
          <strong>Задать вопрос</strong><small>1 глубокий вопрос</small><span className="action-arrow"><ArrowRight /></span>
        </Link>
        <Link className="today-action-card report" to="/reports">
          <span className="action-medallion" aria-hidden="true"><FileText weight="duotone" /><StarFour weight="fill" /></span>
          <img className="action-illustration action-cards-art" src="/assets/today/mystic-cards-v1.webp" width="640" height="640" decoding="async" alt="" aria-hidden="true" />
          <strong>Выбрать разбор</strong><small>7 направлений</small><span className="action-arrow"><ArrowRight /></span>
        </Link>
      </div>
    </section>
    <div className="today-footnote"><span />Ты больше, чем числа<span /></div>
  </div>;
}

function TodayPage() {
  // Calendar loading cells stay div-based: className="day-skeleton" (rendered by TodayView).
  const telegramAuth = useTelegramAuthStatus();
  const miniAppShell = looksLikeTelegramMiniAppShell() || isTelegramMiniApp();
  const telegramAuthSettled = !miniAppShell
    || telegramAuth === 'authenticated'
    || telegramAuth === 'unavailable'
    || telegramAuth === 'failed';
  const [todaySearch] = useSearchParams();
  const profile = useQuery({ queryKey: ['profile'], staleTime: PROFILE_STALE_MS, queryFn: () => api.get<{ user: User }>('/v1/profile') });
  useEffect(() => {
    if (todaySearch.get('src') === 'daily_push') {
      trackFunnelOnce('daily-push-open', 'daily_forecast_push_opened', { sourceSurface: 'daily_forecast' });
    }
  }, [todaySearch]);
  const user = profile.data?.user;
  const localDate = user ? todayInZone(user.timeZone) : '';
  const from = localDate ? addDays(localDate, -3) : '';
  const calendar = useQuery({
    queryKey: ['calendar', from], enabled: Boolean(from) && telegramAuthSettled, staleTime: 60_000, retry: 2,
    queryFn: () => api.get<{ days: CalendarDay[] }>('/v1/profile/numerology/calendar?from=' + from + '&days=7'),
  });
  const forecastKey = [localDate, user?.profileRevision, [...userInterests(user)].sort().join(',')] as const;
  // Checking never calls the model: it only reads a saved forecast (or learns that none is usable).
  const forecast = useQuery({
    queryKey: ['daily-forecast', ...forecastKey], enabled: Boolean(localDate) && telegramAuthSettled, staleTime: 300_000, retry: retryDailyForecast,
    queryFn: () => checkExistingDailyForecast<Forecast>(api, localDate),
  });
  const needsGeneration = forecast.data?.needsGeneration === true;
  const generation = useQuery({
    queryKey: [DAILY_GENERATION_ROOT_KEY, ...forecastKey], ...DAILY_GENERATION_QUERY, enabled: needsGeneration && telegramAuthSettled,
    queryFn: () => generateDailyForecast<Forecast>(api, localDate, idempotencyKey('daily')),
  });
  const generationKey = forecastKey.join('|');
  const ritualShown = useRef(new Set<string>());
  const [revealedKey, setRevealedKey] = useState<string | null>(null);
  const phase = deriveDailyForecastPhase({
    check: forecast, generation, ritualShown: ritualShown.current.has(generationKey), revealed: revealedKey === generationKey,
  });
  if (phase === 'generating') ritualShown.current.add(generationKey);
  if (!user) return <LoadingCard label="Готовим твой день" />;
  const hour = Number(new Intl.DateTimeFormat('ru-RU', { timeZone: user.timeZone, hour: '2-digit', hour12: false }).format(new Date()));
  const greeting = hour < 6 ? 'Доброй ночи' : hour < 12 ? 'Доброе утро' : hour < 18 ? 'Добрый день' : 'Добрый вечер';
  const ready = forecast.data?.forecast ?? generation.data?.forecast;
  const result = phase === 'ready' ? ready?.result : undefined;
  const calendarDays = calendar.data?.days;
  const personalDay = result?.usedCalculationRefs.find(ref => ref.id.endsWith('personalDay'))?.value
    ?? calendarDays?.find(day => day.date === localDate)?.personalDay
    ?? '·';
  const generationFailed = Boolean(forecast.data) && generation.status === 'error';
  const retryForecast = () => generationFailed
    ? requestSingleRetry(generation, () => resetGenerationStart(generationKey))
    : requestSingleRetry(forecast);
  return <div data-calendar-state={calendarDays ? 'ready' : calendar.error ? 'error' : 'loading'}><TodayView
    displayName={user.displayName}
    localDate={localDate}
    greeting={greeting}
    calendarDays={calendarDays}
    dayNumber={personalDay}
    forecast={result ? forecastView(result, personalDay) : undefined}
    phase={phase}
    error={generationFailed ? generation.error : forecast.error}
    generation={phase === 'generating' || phase === 'completing' ? {
      startedAt: generationStartedAt(generationKey, Date.now()),
      onRevealReady: () => setRevealedKey(generationKey),
    } : undefined}
    revealAnimated={revealedKey === generationKey}
    retrying={generation.fetchStatus === 'fetching' || forecast.fetchStatus === 'fetching'}
    calendarError={calendar.error}
    onRetryCalendar={() => void calendar.refetch()}
    onRetryForecast={retryForecast}
    calendarRetryLabel="Повторить календарь"
  /></div>;
}

const PREVIEW_FOCUS_TEXT: Record<InterestId, string> = {
  relationships: 'В отношениях сегодня важнее тон, чем скорость ответа. Если хочется что-то прояснить, начни с того, что чувствуешь, а не с претензии. Близкий человек легче услышит спокойную просьбу, чем намёк. Не додумывай чужие мотивы и оставь место для паузы.',
  money: 'В деньгах день подходит для трезвого взгляда на цифры: что приходит регулярно, что утекает незаметно и где можно назвать свою цену увереннее. Хорошо закрыть одну мелкую финансовую задачу. Не принимай крупное решение из спешки или чужого давления.',
  career: 'В работе полезно выбрать одну задачу, которая реально двигает дело, и довести её до понятного шага. Разговор с коллегой лучше строить на фактах и сроках. Не распыляйся и не бери чужую часть работы только потому, что неловко отказать.',
  self: 'Внутри день просит немного тишины и честности с собой. Заметь, что сейчас даёт силы, а что забирает их по привычке. Одно небольшое действие для себя весит больше, чем длинный список обещаний. Ясности нужно чуть больше времени.',
};

function ContinuationPreviewPage() {
  const [chooserOpen, setChooserOpen] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const questions = [
    { id: 'preview-primary', text: 'Что мне сейчас сделать, чтобы увеличить доход?', angleType: 'MONEY_DIRECTION', role: 'primary' },
    { id: 'preview-dates', text: 'Какие из уже рассчитанных дат ближе к этой теме?', angleType: 'STRONG_DATES', role: 'secondary' },
    { id: 'preview-price', text: 'Где в этой денежной теме сейчас важнее смотреть на цену?', angleType: 'PRICE_OR_VALUE', role: 'secondary' },
  ];
  return <div className="reading-sheet">
    <NumerologistGuide statusLabel="" />
    <section>
      <p className="eyebrow">Вводный разбор</p>
      <ContinuationBridge questions={questions} testId="intro-continuation" onSelect={question => { setSelected(question.text); setChooserOpen(true); }} />
    </section>
    <section>
      <p className="eyebrow">Ответ нумеролога</p>
      <ContinuationBridge questions={questions} testId="chat-continuation" onSelect={question => { setSelected(question.text); setChooserOpen(true); }} />
    </section>
    {chooserOpen && <PaywallOpen onClose={() => setChooserOpen(false)} preservedQuestion={selected ?? undefined} sourceSurface="introductory_reading" angleType="MONEY_DIRECTION" />}
  </div>;
}

function TodayPreviewPage() {
  const [params] = useSearchParams();
  const requested = Number(params.get('number') ?? 8);
  const dayNumber = Number.isInteger(requested) && requested >= 1 && requested <= 22 ? requested : 8;
  const displayName = params.get('name') || 'Валера';
  const requestedInterests = [...new Set((params.get('interests') ?? 'relationships').split(','))]
    .filter((id): id is InterestId => INTERESTS.some(item => item.id === id)).slice(0, MAX_INTERESTS);
  const interests: InterestId[] = requestedInterests.length ? requestedInterests : ['relationships'];
  const values = [5, 6, 7, dayNumber, 9, 1, 2];
  const calendarDays = values.map((personalDay, index) => ({
    date: `2026-01-${String(20 + index).padStart(2, '0')}`,
    personalYear: 3,
    personalMonth: 3,
    personalDay,
  }));
  const fixture: TodayForecastView = {
    title: dayTitle(dayNumber),
    opening: `${displayName}, день ${dayNumber} собирает внимание вокруг выбранных сфер. Общий смысл короткий: числа уже посчитаны, а текст только читает их для сегодняшнего фокуса.`,
    cards: [
      ...interests.map(id => {
        const interest = INTERESTS.find(item => item.id === id)!;
        return { label: interest.cardLabel, body: PREVIEW_FOCUS_TEXT[id], icon: interest.icon };
      }),
      { label: 'Общий фон', body: 'Год и месяц держат один ритм проявления. Сегодня важнее конкретный отклик, чем ещё одно объяснение тех же чисел.', icon: 'sun' },
      { label: 'Осторожнее', body: 'Не превращай потребность в ясности в требование немедленного ответа.', icon: 'warning' },
    ],
    tomorrowTeaser: 'Завтра ритм сменится: после сегодняшней собранности станет больше движения.',
    continuationQuestions: [
      { id: 'preview-primary', text: 'Что мне сейчас сделать, чтобы увеличить доход?', angleType: 'MONEY_DIRECTION', role: 'primary', sourceRefs: ['domain.money'], priorityReason: 'Сначала действие по уже названной боли.' },
      { id: 'preview-dates', text: 'Какие из уже рассчитанных дат ближе к этой теме?', angleType: 'STRONG_DATES', role: 'secondary', sourceRefs: ['strongDates.top'] },
      { id: 'preview-price', text: 'Где в этой денежной теме сейчас важнее смотреть на цену?', angleType: 'PRICE_OR_VALUE', role: 'secondary', sourceRefs: ['domain.money'] },
    ],
  };
  // Dev-only states: ?generation=run|done|error|checking&elapsed=ms&successAfter=ms&hold=1
  const generationMode = params.get('generation');
  const [startedAt] = useState(() => Date.now() - Number(params.get('elapsed') ?? 0));
  const [succeeded, setSucceeded] = useState(generationMode === 'done');
  const [revealed, setRevealed] = useState(false);
  const successAfter = params.get('successAfter');
  useEffect(() => {
    if (successAfter === null) return;
    const timer = window.setTimeout(() => setSucceeded(true), Number(successAfter));
    return () => window.clearTimeout(timer);
  }, [successAfter]);
  const phase: DailyForecastPhase = !generationMode || revealed ? 'ready'
    : generationMode === 'checking' ? 'checking'
      : generationMode === 'error' ? 'error'
        : succeeded ? 'completing' : 'generating';
  return <TodayView displayName={displayName} localDate="2026-01-23" greeting="Добрый день" calendarDays={calendarDays} dayNumber={dayNumber} forecast={fixture}
    phase={phase} revealAnimated={revealed}
    generation={{ startedAt, onRevealReady: () => { if (params.get('hold') !== '1') setRevealed(true); } }}
    onRetryForecast={() => undefined} />;
}

const NUMEROLOGIST_FALLBACK_QUESTIONS = [
  'Какие даты сентября лучше подойдут для разговоров о внимании?',
  'Как мне лучше сформулировать этот разговор без давления?',
  'Тебе важнее выбрать дату для первого разговора или для закрепления договорённости?',
] as const;

function NumerologistHero() {
  return <section className="numerologist-hero">
    <img
      className="numerologist-hero-art"
      src="/assets/numerologist/hero-reference-v3.webp"
      width="843"
      height="450"
      decoding="async"
      fetchPriority="high"
      alt=""
      aria-hidden="true"
    />
    <span className="numerologist-close-mask" aria-hidden="true" />
    <span className="numerologist-controls-mask" aria-hidden="true" />
    <h1 className="numerologist-hero-heading">Спроси о важном</h1>
  </section>;
}

function NumerologistGuide({ statusLabel }: { statusLabel: string }) {
  return <section className="numerologist-guide">
    <div className="numerologist-guide-mark" aria-hidden="true"><StarFour weight="fill" /></div>
    <div className="numerologist-guide-title">
      <strong>Нумеролог</strong>
      <span>готов продолжить разбор.</span>
      {statusLabel && <small className="count-chip numerologist-status">{statusLabel}</small>}
    </div>
    <div className="numerologist-guide-seal" aria-hidden="true">Сила<br />в твоих<br />числах</div>
    <p>Задавай вопросы о любви, отношениях, работе, финансах и предназначении. Ответ будет опираться на твою дату рождения и уже пройденный разбор.</p>
  </section>;
}

function NumerologistExamples({ questions, onSelect }: { questions: string[]; onSelect: (question: string) => void }) {
  const icons = [<Heart weight="duotone" />, <Briefcase weight="duotone" />, <UserCircle weight="duotone" />];
  return <section className="numerologist-examples">
    <h2><StarFour weight="fill" />Примеры вопросов</h2>
    <div>
      {questions.map((question, index) => <button key={question} onClick={() => onSelect(question)}>
        <span className="numerologist-example-icon" aria-hidden="true">{icons[index % icons.length]}</span>
        <span>{question}</span>
        <CaretRight aria-hidden="true" />
      </button>)}
    </div>
  </section>;
}

function NumerologistPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: 'auto' }); }, []);
  const incoming = location.state as {
    question?: string; messageOrigin?: string; autoSend?: boolean;
    sourceSurface?: ContinuationSurface; angleType?: string; questionRank?: 'primary' | 'secondary';
  } | null;
  const [text, setText] = useState(incoming?.question ?? '');
  const sourceSuggestionRef = useRef<string | null>(incoming?.question ?? null);
  const pendingQuestionRef = useRef<string | null>(incoming?.question ?? null);
  const autoSentRef = useRef(false);
  const [localMessages, setLocalMessages] = useState<Message[]>([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [preservedQuestion, setPreservedQuestion] = useState<string | null>(incoming?.autoSend ? incoming.question ?? null : null);
  const [paywallSource, setPaywallSource] = useState<{ sourceSurface?: ContinuationSurface; angleType?: string; questionRank?: 'primary' | 'secondary' }>({
    sourceSurface: incoming?.sourceSurface, angleType: incoming?.angleType, questionRank: incoming?.questionRank,
  });
  const endRef = useRef<HTMLDivElement>(null);
  const history = useQuery({ queryKey: ['messages'], queryFn: () => api.get<{ messages: Message[] }>('/v1/ai/conversations/current/messages') });
  const entitlement = useQuery({
    queryKey: ['entitlements'],
    queryFn: () => api.get<{ entitlements: { remaining: number; reserved: number }[]; balance: EntitlementBalance }>('/v1/ai/entitlements'),
  });
  const reading = useQuery({ queryKey: ['reading-status'], queryFn: () => api.get<{ introductoryReading: { latest: Reading | null } }>('/v1/ai/introductory-reading/status') });
  useEffect(() => { if (history.data) setLocalMessages(history.data.messages); }, [history.data]);
  useEffect(() => {
    if (historyOpen) endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [historyOpen, localMessages]);
  const applySuggestion = (question: string) => {
    setText(question);
    sourceSuggestionRef.current = question;
  };
  const send = useMutation({
    mutationFn: async (question: string) => {
      await api.post('/v1/ai/conversations', {});
      const messageOrigin = resolveComposerOrigin(question, sourceSuggestionRef.current);
      return api.post<{ message: Message; answer: { answer: string; takeaway: string; followUpQuestions: string[] }; entitlement: { remaining: number }; balance: EntitlementBalance }>(
        '/v1/ai/conversations/current/messages',
        {
          text: question,
          messageOrigin,
          ...(sourceSuggestionRef.current ? { sourceSuggestion: sourceSuggestionRef.current } : {}),
        },
        idempotencyKey('chat'),
      );
    },
    onMutate: question => {
      pendingQuestionRef.current = question;
      setLocalMessages(messages => [...messages, { messageId: 'pending-user', role: 'user', text: question, structuredResult: null, createdAt: new Date().toISOString() }]);
      setText('');
      sourceSuggestionRef.current = null;
    },
    onSuccess: data => {
      if (pendingQuestionRef.current) clearPendingContinuation(pendingQuestionRef.current);
      setLocalMessages(messages => [...messages.filter(message => message.messageId !== 'pending-assistant'), data.message]);
      queryClient.invalidateQueries({ queryKey: ['messages'] }); queryClient.invalidateQueries({ queryKey: ['entitlements'] });
    },
    onError: error => {
      setLocalMessages(messages => messages.filter(message => !message.messageId.startsWith('pending-')));
      if (error instanceof ApiError && error.status === 402) {
        const question = pendingQuestionRef.current;
        if (question) {
          setPreservedQuestion(question);
          setText(question);
          setPaywallSource(current => current.sourceSurface ? current : { sourceSurface: 'chat' });
        }
        setPaywallOpen(true);
      }
    },
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const question = text.trim();
    if (question && !send.isPending) {
      setHistoryOpen(true);
      send.mutate(question);
    }
  };
  const balance = entitlement.data?.balance;
  useEffect(() => {
    if (!incoming?.autoSend || !incoming.question || autoSentRef.current || !entitlement.isSuccess) return;
    autoSentRef.current = true;
    // A later back/forward visit to this history entry must not send the question again.
    navigate(location.pathname, { replace: true, state: { ...incoming, autoSend: false } });
    const state = entitlementAnalyticsState(balance);
    void trackFunnel(chatOpenedEventFor(incoming.sourceSurface), {
      sourceSurface: incoming.sourceSurface, angleType: incoming.angleType, entitlementState: state,
    });
    if (balance?.canAskQuestion) {
      setHistoryOpen(true);
      sourceSuggestionRef.current = incoming.question;
      send.mutate(incoming.question);
    } else {
      setPreservedQuestion(incoming.question);
      setText(incoming.question);
      setPaywallOpen(true);
    }
  }, [incoming, entitlement.isSuccess, balance, send]);
  const canAsk = balance?.canAskQuestion ?? ((entitlement.data?.entitlements[0]?.remaining ?? 0) > 0);
  const needsPayment = entitlement.isSuccess && !canAsk;
  const openPaywall = () => setPaywallOpen(true);
  const answered = localMessages.some(message => message.role === 'assistant');
  const statusLabel = entitlementStatusLabel(balance) || `${entitlement.data?.entitlements[0]?.remaining ?? 0} вопрос`;
  const latestResult = reading.data?.introductoryReading.latest?.result;
  const readingPrompts = latestResult?.schemaVersion === '3.0.0'
    ? latestResult.continuationQuestions.map(item => item.text)
    : latestResult && 'starterQuestions' in latestResult ? latestResult.starterQuestions : [];
  const starterQuestions = Array.from(new Set([
    ...readingPrompts,
    ...NUMEROLOGIST_FALLBACK_QUESTIONS,
  ])).slice(0, 3);
  const continueAnswer = (question: string, angleType?: string, questionRank: 'primary' | 'secondary' = 'secondary') => {
    const surface: ContinuationSurface = (balance?.freeQuestionsRemaining ?? 0) > 0 ? 'free_question' : 'chat';
    rememberPendingContinuation({ question, sourceSurface: surface, angleType, questionRank });
    void trackFunnel(surface === 'free_question' ? 'free_question_continuation_clicked' : 'chat_opened_from_continuation', {
      sourceSurface: surface, angleType, entitlementState: entitlementAnalyticsState(balance), questionRank,
    });
    sourceSuggestionRef.current = question;
    pendingQuestionRef.current = question;
    setPaywallSource({ sourceSurface: surface, angleType, questionRank });
    if (canAsk && !send.isPending) {
      setHistoryOpen(true);
      send.mutate(question);
      return;
    }
    setPreservedQuestion(question);
    setText(question);
    setPaywallOpen(true);
  };
  const landingVisible = !historyOpen;
  const hasHistory = localMessages.length > 0;
  return <div className="chat-page numerologist-page">
    {landingVisible ? <>
      <NumerologistHero />
      <div className="numerologist-content">
        <NumerologistGuide statusLabel={statusLabel} />
        <NumerologistExamples questions={starterQuestions} onSelect={applySuggestion} />
      </div>
    </> : <div className="numerologist-content numerologist-conversation-header">
      <PageHeader eyebrow="Нумеролог" title="Спроси о важном"><span className="count-chip">{statusLabel}</span></PageHeader>
    </div>}
    {historyOpen && <div className="chat-list">
      {localMessages.map(message => <div key={message.messageId} className={'message-row ' + message.role}><div className="message-bubble">{message.text}
        {message.role === 'assistant' && Boolean(message.structuredResult?.timeline?.length) && <div className="chat-timeline">{message.structuredResult!.timeline!.map(item => <div key={item.period}><strong>{item.period} · {item.number}</strong><span>{item.text}</span></div>)}</div>}
        {message.role === 'assistant' && message.structuredResult?.openLoop && <p className="chat-open-loop">{message.structuredResult.openLoop}</p>}
        <time>{new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit' }).format(new Date(message.createdAt))}</time>
        {message.role === 'assistant' && <ChatContinuations message={message} onSelect={continueAnswer} />}
      </div></div>)}
      {send.isPending && <div className="message-row assistant"><div className="assistant-bubble typing"><span /><span /><span /><small>Сверяю ответ с твоими числами</small></div></div>}
      <ErrorNotice error={send.error instanceof ApiError && send.error.status === 402 ? null : send.error} /><div ref={endRef} />
    </div>}
    {needsPayment && (landingVisible || answered) && <ProductOffers compact exhausted />}
    <div className="numerologist-composer-stack">
      {needsPayment
        ? <form className="chat-composer composer-locked" onSubmit={event => { event.preventDefault(); openPaywall(); }}><span className="composer-spark" aria-hidden="true"><StarFour weight="fill" /></span><textarea rows={1} value="" readOnly onClick={openPaywall} placeholder="Задай следующий вопрос - выбери пакет" aria-label="Задать следующий вопрос" /><button type="submit" aria-label="Выбрать пакет вопросов"><ArrowRight weight="bold" /></button></form>
        : <form className="chat-composer" onSubmit={submit}><span className="composer-spark" aria-hidden="true"><StarFour weight="fill" /></span><textarea rows={1} value={text} onChange={event => setText(event.target.value)} placeholder="Задай свой вопрос…" disabled={!canAsk || send.isPending} maxLength={2000} /><button aria-label="Отправить" disabled={!text.trim() || !canAsk || send.isPending}><PaperPlaneRight weight="fill" /></button></form>}
      {landingVisible && hasHistory && <button type="button" className="numerologist-history-button" onClick={() => setHistoryOpen(true)}><ChatCircleDots weight="duotone" /><span>История диалога</span><CaretRight /></button>}
    </div>
    {paywallOpen && <PaywallOpen onClose={() => setPaywallOpen(false)} preservedQuestion={preservedQuestion ?? undefined} sourceSurface={paywallSource.sourceSurface} angleType={paywallSource.angleType} questionRank={paywallSource.questionRank} />}
  </div>;
}

function ChatContinuations({ message, onSelect }: { message: Message; onSelect: (question: string, angleType?: string, rank?: 'primary' | 'secondary') => void }) {
  const structured = message.structuredResult;
  const questions = structured?.continuationQuestions?.length
    ? structured.continuationQuestions.map(item => ({ id: item.id, text: item.text, angleType: item.angleType, role: item.role }))
    : (structured?.followUpQuestions ?? []).map(text => ({ text, angleType: undefined as string | undefined }));
  useEffect(() => {
    if (!questions.length) return;
    const surface: ContinuationSurface = 'free_question';
    trackFunnelOnce(`answer-view:${message.messageId}`, 'free_question_answer_viewed', { sourceSurface: surface });
    trackFunnelOnce(`answer-bridge:${message.messageId}`, 'free_question_continuation_impression', {
      sourceSurface: surface, angleType: questions[0]?.angleType, questionRank: 'primary',
    });
  }, [message.messageId, questions.length, questions[0]?.angleType]);
  if (!questions.length) return null;
  return <ContinuationBridge questions={questions} testId="chat-continuation" onSelect={(question, rank) => onSelect(question.text, question.angleType, rank)} />;
}

function PaywallOpen({ onClose, preservedQuestion, sourceSurface, angleType, questionRank }: {
  onClose: () => void; preservedQuestion?: string; sourceSurface?: ContinuationSurface; angleType?: string;
  questionRank?: 'primary' | 'secondary';
}) {
  const navigate = useNavigate();
  return <PaywallSheet
    onClose={onClose}
    preservedQuestion={preservedQuestion}
    sourceSurface={sourceSurface}
    angleType={angleType}
    questionRank={questionRank}
    onSelect={productId => {
      onClose();
      const params = new URLSearchParams({ productId });
      params.set('direct', '1');
      if (sourceSurface) params.set('sourceSurface', sourceSurface);
      if (angleType) params.set('angleType', angleType);
      if (questionRank) params.set('questionRank', questionRank);
      navigate(`/checkout?${params.toString()}`);
    }}
  />;
}

const REPORT_META: Record<string, { description: string; accent: string; symbol: ReactNode; includes: string[] }> = {
  RELATIONSHIPS_V1: {
    description: 'Большой разбор твоей близости: сценарий, границы и то, что усиливает текущий период.',
    accent: 'rose', symbol: <Heart weight="fill" />,
    includes: ['Базовый стиль близости', 'Повторяющийся сценарий', 'Ближайший период и итог'],
  },
  COMPATIBILITY_V1: {
    description: 'Как вы сближаетесь, где различается ваш ритм и что помогает лучше понимать друг друга.',
    accent: 'violet', symbol: <Sparkle weight="fill" />,
    includes: ['Что каждому нужно в близости', 'Где ритмы сходятся и расходятся', 'Общение, границы и синтез'],
  },
  MONEY_V1: {
    description: 'Денежный сценарий: через что доходу легче расти и где сейчас окно периода.',
    accent: 'gold', symbol: <Wallet weight="fill" />,
    includes: ['Денежный рисунок', 'Сильные стороны заработка', 'Главная возможность периода'],
  },
  CAREER_V1: {
    description: 'Предназначение и реализация: где силы раскрываются сильнее в работе и деле.',
    accent: 'blue', symbol: <Briefcase weight="fill" />,
    includes: ['Паттерн реализации', 'Рабочий стиль', 'Личный итог периода'],
  },
  PERSONAL_YEAR_V1: {
    description: 'Полный разбор выбранного календарного года: тема, месяцы и важные переходы.',
    accent: 'orange', symbol: <CalendarBlank weight="fill" />,
    includes: ['Главная тема года', 'Ритм двенадцати месяцев', 'На что смотреть особенно'],
  },
  PSYCHOMATRIX_V1: {
    description: 'Твои сильные клетки, внутренние опоры, напряжения и восемь линий матрицы.',
    accent: 'mint', symbol: <span className="matrix-symbol">3×3</span>,
    includes: ['Выраженные и тихие клетки', 'Восемь линий', 'Ресурсы, напряжения и синтез'],
  },
  MATRIX22_V1: {
    description: 'Центр, личные опоры, близость, ресурсы и повторяющиеся энергии твоей матрицы.',
    accent: 'plum', symbol: <span className="matrix-symbol">28</span>,
    includes: ['Центр и четыре опоры', 'Близость и ресурсы', 'Повторяющиеся энергии'],
  },
};

const REPORT_CONTEXT_FALLBACK: Record<string, { question: string; options: { id: string; label: string }[] }> = {
  COMPATIBILITY_V1: {
    question: 'Что сейчас между вами?',
    options: [
      { id: 'together', label: 'Мы вместе' },
      { id: 'difficult_period', label: 'Сложный период' },
      { id: 'just_met', label: 'Только познакомились' },
    ],
  },
  RELATIONSHIPS_V1: {
    question: 'Что в личной жизни сейчас волнует сильнее всего?',
    options: [
      { id: 'constant_fights', label: 'Постоянные ссоры' },
      { id: 'distance', label: 'Чувствую дистанцию' },
      { id: 'cant_let_go', label: 'Не могу отпустить прошлые отношения' },
      { id: 'understand_pattern', label: 'Хочу понять свой сценарий отношений' },
      { id: 'no_specific', label: 'Пока без конкретной ситуации' },
    ],
  },
  MONEY_V1: {
    question: 'Что сейчас важнее всего в деньгах?',
    options: [
      { id: 'raise_income', label: 'Увеличить доход' },
      { id: 'raise_price', label: 'Поднять цену' },
      { id: 'unstable', label: 'Нестабильный доход' },
      { id: 'own_business', label: 'Своё дело' },
      { id: 'money_strengths', label: 'Понять денежные сильные стороны' },
      { id: 'no_specific', label: 'Без конкретной ситуации' },
    ],
  },
  CAREER_V1: {
    question: 'Что сейчас важнее всего в работе и реализации?',
    options: [
      { id: 'change_job', label: 'Сменить работу' },
      { id: 'own_project', label: 'Развивать своё дело' },
      { id: 'strengths', label: 'Понять сильные стороны' },
      { id: 'find_direction', label: 'Найти направление' },
      { id: 'career_stuck', label: 'Чувствую карьерный тупик' },
      { id: 'general', label: 'Общий разбор' },
    ],
  },
  PERSONAL_YEAR_V1: {
    question: 'Какая сфера года интересует больше?',
    options: [
      { id: 'money', label: 'Деньги' },
      { id: 'relationships', label: 'Отношения' },
      { id: 'career', label: 'Работа' },
      { id: 'changes', label: 'Перемены' },
      { id: 'whole_year', label: 'Хочу увидеть весь год' },
    ],
  },
};

const REPORT_PROGRESS_FALLBACK = REPORT_LOADING_PHRASES;

const MONTH_NAMES_RU = ['январь', 'февраль', 'март', 'апрель', 'май', 'июнь', 'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь'];

function reportStatusLabel(status: string): string {
  switch (status) {
    case 'completed': return 'Готов';
    case 'generation_failed': return 'Разбор готовится чуть дольше обычного';
    case 'generating':
    case 'ready_to_generate':
    case 'purchased':
    case 'awaiting_input':
      return 'Готовлю';
    case 'canceled': return 'Отменён';
    case 'revoked': return 'Отозван';
    default: return 'В работе';
  }
}

function catalogCardStatus(product: Product): { eyebrow: string; priceLabel: string; cta: string; purchaseSoon: boolean } {
  if (product.checkoutEnabled) {
    return { eyebrow: 'Большой разбор', priceLabel: product.price?.formatted ?? '', cta: 'Посмотреть', purchaseSoon: false };
  }
  if (product.availability === 'coming_soon' || product.type === 'coming_soon') {
    return { eyebrow: 'Скоро', priceLabel: 'Скоро', cta: 'Подробнее', purchaseSoon: false };
  }
  return {
    eyebrow: product.badge ?? 'Разбор готов',
    priceLabel: product.price?.formatted ?? 'Скоро откроем покупку',
    cta: 'Подробнее',
    purchaseSoon: true,
  };
}

function Narrative({ text }: { text: string | undefined | null }) {
  const parts = splitNarrativeParagraphs(text);
  if (!parts.length) return null;
  return <>{parts.map((part, index) => <p key={index}>{part}</p>)}</>;
}

function phaseBadgeLabel(phase: string | undefined): string {
  return localizeMachineLabel(phase);
}

function ReportCard({ product }: { product: Product }) {
  const meta = REPORT_META[product.productId] ?? { description: product.scope ?? '', accent: 'violet', symbol: <FileText />, includes: [] };
  const status = catalogCardStatus(product);
  const artSlug = product.productId.toLowerCase().replace(/_v1$/, '');
  return <Link to={'/reports/' + product.productId} className={'report-card ' + meta.accent + (status.purchaseSoon ? ' purchase-soon' : '')}>
    <div className="report-art" style={{ '--report-art': `url(/assets/reports/reference-v2/tile-${artSlug}.webp)` } as CSSProperties} />
    <div className="report-copy">
      <p className="eyebrow">{status.eyebrow}</p>
      <h2>{product.title}</h2>
      <p>{meta.description}</p>
      <footer>
        <strong>{status.priceLabel}</strong>
        <span>{status.cta} <CaretRight /></span>
      </footer>
    </div>
    <ReportVisual productId={product.productId} />
  </Link>;
}

function ReportVisual({ productId, mine = false }: { productId: string; mine?: boolean }) {
  const artSlug = productId.toLowerCase().replace(/_v1$/, '');
  const asset = mine ? `mine-${artSlug}` : `right-${artSlug}`;
  return <div className={'report-card-visual visual-' + artSlug} style={{ '--report-visual': `url(/assets/reports/reference-v2/${asset}.webp)` } as CSSProperties} aria-hidden="true" />;
}

function MineReportCard({ item }: { item: PremiumReportListItem }) {
  const navigate = useNavigate();
  const meta = REPORT_META[item.productId] ?? { description: '', accent: 'violet', symbol: <FileText />, includes: [] };
  const dateValue = item.purchasedAt ?? item.generatedAt ?? item.createdAt;
  const open = () => navigate(`/reports/view/${encodeURIComponent(item.reportId)}`);
  const retry = useMutation({
    mutationFn: () => api.post<PremiumReportDetail>(`/v1/reports/${item.reportId}/retry`, {}),
    onSuccess: () => navigate(`/reports/generating/${encodeURIComponent(item.reportId)}`),
  });
  return <article className={'mine-report-card ' + meta.accent}>
    <div className="mine-report-copy">
      <div className="mine-report-top">
        <p className="eyebrow">{reportStatusLabel(item.status)}</p>
        {item.targetYear != null && <span className="year-chip">{item.targetYear}</span>}
      </div>
      <h2>{item.title}</h2>
      <small>{new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(dateValue))}</small>
    </div>
    <ReportVisual productId={item.productId} mine />
    {item.previewText && <p className="mine-preview">{item.previewText}</p>}
    {item.status === 'generation_failed' && item.productId !== 'COMPATIBILITY_V1' ? (
      <button className="primary-button mine-report-open" disabled={retry.isPending} onClick={() => retry.mutate()}>
        {retry.isPending ? 'Запускаю...' : item.actionLabel || 'Попробовать ещё раз'}
      </button>
    ) : (
      <button className="secondary-button mine-report-open" onClick={open}>
        {item.status === 'completed' ? (item.actionLabel || 'Открыть') : (item.actionLabel || 'Готовлю разбор')} <CaretRight />
      </button>
    )}
    <ErrorNotice error={retry.error} />
  </article>;
}

const REPORTS_PREVIEW_PRODUCTS: Product[] = [
  ['RELATIONSHIPS_V1', 'Большой разбор отношений', 'available', true, '490 ₽', null],
  ['COMPATIBILITY_V1', 'Совместимость пары', 'available', true, '590 ₽', null],
  ['MONEY_V1', 'Деньги и финансовый сценарий', 'available', true, '490 ₽', null],
  ['CAREER_V1', 'Предназначение и реализация', 'available', true, '490 ₽', null],
  ['PERSONAL_YEAR_V1', 'Персональный год', 'available', true, '390 ₽', null],
  ['PSYCHOMATRIX_V1', 'Психоматрица', 'preview_only', false, '490 ₽', 'Готово · покупка скоро'],
  ['MATRIX22_V1', 'Матрица судьбы: 22 архетипа', 'preview_only', false, '590 ₽', 'Готово · покупка скоро'],
].map(([productId, title, availability, checkoutEnabled, formatted, badge]) => ({
  productId: String(productId), title: String(title), type: 'report', scope: null,
  availability: availability as Product['availability'], checkoutEnabled: Boolean(checkoutEnabled),
  price: { minor: Number(String(formatted).replace(/\D/g, '')) * 100, currency: 'RUB', formatted: String(formatted) },
  badge: badge == null ? null : String(badge),
}));

const REPORTS_PREVIEW_MINE: PremiumReportListItem[] = [{
  reportId: 'preview-relationships', productId: 'RELATIONSHIPS_V1', title: 'Большой разбор отношений', status: 'completed', actionLabel: 'Открыть', targetYear: null,
  previewText: 'Валера, в основе твоего запроса видна не потребность спорить, а сильная потребность в надёжности и понятной связи. Для тебя близость особенно убедительна тогда, когда слова подтверждаются поступками, договорённости соблюдаются, а участие другого человека не приходится постоянно доказывать...',
  generatedAt: '2026-09-18T10:00:00Z', createdAt: '2026-09-18T10:00:00Z', purchasedAt: '2026-09-18T10:00:00Z',
}];

function ReportsPage() {
  const preview = useLocation().pathname === '/__preview/reports';
  const [params, setParams] = useSearchParams();
  const initialTab = params.get('tab') === 'mine' ? 'mine' : 'catalog';
  const [tab, setTab] = useState<'catalog' | 'mine'>(initialTab);
  const catalog = useQuery({
    queryKey: ['catalog'],
    enabled: !preview,
    initialData: preview ? { products: REPORTS_PREVIEW_PRODUCTS } : undefined,
    staleTime: 300_000,
    queryFn: () => api.get<{ products: Product[] }>('/v1/catalog'),
  });
  const mine = useQuery({
    queryKey: ['my-reports'],
    enabled: tab === 'mine' && !preview,
    initialData: preview ? { reports: REPORTS_PREVIEW_MINE } : undefined,
    staleTime: REPORTS_LIST_STALE_MS,
    queryFn: () => api.get<{ reports: PremiumReportListItem[] }>('/v1/reports'),
  });
  useEffect(() => {
    if (!preview && tab === 'catalog') void trackFunnel('report_catalog_viewed');
  }, [preview, tab]);
  const reports = catalog.data?.products.filter(product => product.type === 'report') ?? [];
  const selectTab = (next: 'catalog' | 'mine') => {
    setTab(next);
    const nextParams = new URLSearchParams(params);
    if (next === 'mine') nextParams.set('tab', 'mine');
    else nextParams.delete('tab');
    setParams(nextParams, { replace: true });
  };
  const mineList = mine.data?.reports;
  return <div className="reports-page">
    <header className="reports-hero">
      <p className="eyebrow">Глубинные материалы</p>
      <h1>Разборы</h1>
      <p>Больше понимания. Ближе к себе.</p>
    </header>
    <div className="segmented">
      <button className={tab === 'catalog' ? 'active' : ''} onClick={() => selectTab('catalog')}>Каталог</button>
      <button className={tab === 'mine' ? 'active' : ''} onClick={() => selectTab('mine')}>Мои разборы</button>
    </div>
    {tab === 'mine' ? (
      mine.isPending && !mineList ? <LoadingCard label="Открываем твои разборы" /> :
      !(mineList?.length) ? (
        <section className="empty-state">
          <div><FileText /></div>
          <h2>Здесь появятся твои разборы</h2>
          <p>После покупки большой разбор останется здесь - можно вернуться к нему в любой момент.</p>
          <button className="secondary-button" onClick={() => selectTab('catalog')}>Открыть каталог</button>
        </section>
      ) : (
        <div className="mine-report-list">
          {mineList.map(item => <MineReportCard key={item.reportId} item={item} />)}
          <ErrorNotice error={mine.error} />
        </div>
      )
    ) : <>
      <section className="catalog-intro"><div className="catalog-orbit-art" aria-hidden="true" /><div><p className="eyebrow">Глубже в одну тему</p><h2>Выбери фокус большого разбора</h2><p>Каждый разбор строится на твоих числах и остаётся у тебя после покупки.</p></div><div className="catalog-seal-art" aria-hidden="true" /></section>
      {catalog.isPending && !catalog.data ? <LoadingCard label="Открываем каталог" /> : <div className="report-list">{reports.map(product => <ReportCard key={product.productId} product={product} />)}</div>}
      <ErrorNotice error={catalog.error} />
    </>}
  </div>;
}

function ReportProductPage() {
  const { productId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [contextChoice, setContextChoice] = useState('');
  const [targetYear, setTargetYear] = useState<number | null>(null);
  const [disabledModal, setDisabledModal] = useState(false);
  const isCompatibility = productId === 'COMPATIBILITY_V1';
  const [partnerName, setPartnerName] = useState('');
  const [partnerDob, setPartnerDob] = useState<{ day: number | null; month: number | null; year: number | null }>({ day: null, month: null, year: null });
  const [partnerError, setPartnerError] = useState<string | null>(null);
  const partnerBirthDate = partnerDob.day !== null && partnerDob.month !== null && partnerDob.year !== null
    ? `${partnerDob.year}-${String(partnerDob.month).padStart(2, '0')}-${String(partnerDob.day).padStart(2, '0')}`
    : null;
  const telegramAuth = useTelegramAuthStatus();
  const catalog = useQuery({ queryKey: ['catalog'], staleTime: 300_000, queryFn: () => api.get<{ products: Product[] }>('/v1/catalog') });
  const auth = useQuery({ queryKey: ['auth-me'], queryFn: () => api.get<{ hasStableIdentity: boolean; user: { displayName: string } | null }>('/v1/auth/me') });
  const runtime = useQuery({ queryKey: ['runtime'], queryFn: () => api.get<{ fakeAuthEnabled: boolean }>('/v1/runtime') });
  const product = catalog.data?.products.find(item => item.productId === productId);
  const [notReadyMessage, setNotReadyMessage] = useState<string | null>(null);
  const partnerFormStarted = useRef(false);
  useEffect(() => {
    if (!product) return;
    void trackFunnel('product_viewed', { productId, source: 'report' });
    if (isCompatibility) void trackFunnel('compatibility_opened', { productId, source: 'report' });
  }, [product?.productId, productId]);
  const editPartner = () => {
    setPartnerError(null);
    setNotReadyMessage(null);
    if (!partnerFormStarted.current) {
      partnerFormStarted.current = true;
      void trackFunnel('compatibility_partner_form_started', { productId, source: 'report' });
    }
  };
  const fallback = REPORT_CONTEXT_FALLBACK[productId];
  const prepare = useQuery({
    queryKey: ['report-prepare', productId],
    enabled: Boolean(product?.checkoutEnabled && isReportProductId(productId) && !isCompatibility),
    queryFn: () => api.post<ReportPrepareResult>('/v1/reports/prepare', { productId }),
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!prepare.data) return;
    if (!contextChoice && prepare.data.contextOptions[0]) setContextChoice(prepare.data.contextOptions[0].id);
    if (prepare.data.requiresTargetYear && targetYear == null) {
      setTargetYear(prepare.data.targetYear ?? prepare.data.yearOptions?.defaultYear ?? null);
    }
  }, [prepare.data, contextChoice, targetYear]);

  useEffect(() => {
    if (contextChoice || !fallback?.options[0]) return;
    setContextChoice(fallback.options[0].id);
  }, [fallback, contextChoice]);

  const login = useMutation({
    mutationFn: async () => {
      const returnPath = `/reports/${encodeURIComponent(productId)}`;
      if (runtime.data?.fakeAuthEnabled) {
        return api.post('/v1/auth/telegram/fake-login', {
          subject: `web_${crypto.randomUUID().slice(0, 12)}`,
          firstName: auth.data?.user?.displayName ?? 'Пользователь',
          productId,
          returnPath,
        });
      }
      const started = await api.post<{ authorizationUrl: string }>('/v1/auth/telegram/start', { productId, returnPath });
      window.location.assign(started.authorizationUrl);
      return started;
    },
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: ['auth-me'] }); },
  });

  const startCheckout = useMutation({
    onError: error => {
      if (isCompatibility && error instanceof ApiError && error.code === 'COMPATIBILITY_NOT_READY') {
        setNotReadyMessage(error.message);
        return;
      }
      trackCheckoutError(productId, error);
    },
    mutationFn: async () => {
      void trackFunnel('offer_selected', { productId, source: 'report' });
      if (!product) throw new Error('Продукт не найден.');
      const body: {
        productId: string; contextChoice?: string; targetYear?: number;
        partnerDisplayName?: string; partnerBirthDate?: string; relationshipContext?: string;
      } = { productId };
      if (contextChoice) body.contextChoice = contextChoice;
      if (productId === 'PERSONAL_YEAR_V1' && targetYear != null) body.targetYear = targetYear;
      if (isCompatibility && partnerBirthDate) {
        body.partnerDisplayName = partnerName.trim();
        body.partnerBirthDate = partnerBirthDate;
        if (contextChoice) body.relationshipContext = contextChoice;
      }
      const prepared = await api.post<ReportPrepareResult>('/v1/reports/prepare', body);
      if (prepared.alreadyOwned && prepared.reportId) {
        navigate(`/reports/view/${encodeURIComponent(prepared.reportId)}`);
        return prepared;
      }
      if (!prepared.prepareId) throw new Error('Не удалось подготовить покупку разбора.');
      const next = new URLSearchParams({
        productId,
        prepareId: prepared.prepareId,
      });
      if (contextChoice) next.set('contextChoice', contextChoice);
      if (prepared.targetYear != null) next.set('targetYear', String(prepared.targetYear));
      navigate(`/checkout?${next.toString()}`);
      return prepared;
    },
  });

  const buyReport = () => {
    if (isCompatibility) {
      if (!partnerName.trim()) { setPartnerError('Напиши, как зовут второго человека.'); return; }
      if (!partnerBirthDate) { setPartnerError('Укажи дату рождения второго человека.'); return; }
      void trackFunnel('compatibility_partner_form_completed', { productId, source: 'report' });
    }
    setPartnerError(null);
    setNotReadyMessage(null);
    startCheckout.mutate();
  };

  if (catalog.isPending && !catalog.data) return <LoadingCard label="Открываем разбор" />;
  if (!product) return <div className="empty-state"><h2>Разбор не найден</h2><button className="secondary-button" onClick={() => navigate('/reports')}>Назад</button></div>;
  const meta = REPORT_META[product.productId] ?? { description: product.scope ?? '', accent: 'violet', symbol: <FileText />, includes: [] };
  const contextQuestion = prepare.data?.contextQuestion ?? fallback?.question ?? 'Что сейчас важнее всего в этой теме?';
  const contextOptions = prepare.data?.contextOptions ?? fallback?.options ?? [];
  const yearOptions = prepare.data?.yearOptions;
  const alreadyOwned = prepare.data?.alreadyOwned && prepare.data.reportId;
  const telegramBootstrapPending = looksLikeTelegramMiniAppShell() && isTelegramAuthPending(telegramAuth);
  const insideTelegramShell = looksLikeTelegramMiniAppShell() || isTelegramMiniApp() || telegramAuth === 'authenticated' || telegramAuth === 'authenticating';
  const needsLogin = auth.data && !auth.data.hasStableIdentity && !insideTelegramShell && !telegramBootstrapPending;
  const softNote = !product.checkoutEnabled
    ? (product.availability === 'coming_soon' || product.type === 'coming_soon'
      ? 'Этот разбор ещё готовится.'
      : 'Разбор уже готов по содержанию. Покупка откроется чуть позже.')
    : null;
  const status = catalogCardStatus(product);

  return <div className="product-page">
    <button className="back-link" onClick={() => navigate(-1)}><ArrowLeft /> Каталог</button>
    <section className={'product-hero ' + meta.accent}>
      <div className="product-symbol">{meta.symbol}</div>
      <p className="eyebrow">{status.eyebrow}</p>
      <h1>{product.title}</h1>
      <p>{meta.description}</p>
      <strong>{status.priceLabel}</strong>
    </section>
    <section className="product-section">
      <p className="eyebrow">Что внутри</p>
      <h2>Связный разбор без мистического тумана</h2>
      <div className="include-list">{meta.includes.map((item, index) => <div key={item}><span>0{index + 1}</span><strong>{item}</strong><Check /></div>)}</div>
    </section>
    {product.checkoutEnabled && isCompatibility && (
      <section className="product-section partner-section">
        <p className="eyebrow">Второй человек</p>
        <h2>С кем смотрим совместимость?</h2>
        <label className="field-block">
          <span>Имя</span>
          <input value={partnerName} onChange={event => { setPartnerName(event.target.value); editPartner(); }} placeholder="Например, Максим" maxLength={40} autoComplete="off" />
        </label>
        <p className="eyebrow">Дата рождения</p>
        <DateOfBirthPicker
          day={partnerDob.day}
          month={partnerDob.month}
          year={partnerDob.year}
          maxYear={new Date().getFullYear()}
          onChange={next => { setPartnerDob(next); editPartner(); }}
        />
        {partnerError && <p className="purchase-caption field-error-text" role="alert">{partnerError}</p>}
        <p className="purchase-caption">Твою дату я уже знаю из профиля. Дата второго человека нужна только для этого разбора и не попадает в твой профиль.</p>
      </section>
    )}
    {product.checkoutEnabled && (
      <section className="product-section context-section">
        <p className="eyebrow">Контекст</p>
        <h2>{contextQuestion}</h2>
        <div className="context-chips">
          {contextOptions.map(option => (
            <button key={option.id} type="button" className={contextChoice === option.id ? 'selected' : ''} onClick={() => { setContextChoice(option.id); if (isCompatibility) editPartner(); }}>
              {option.label}
            </button>
          ))}
        </div>
        {productId === 'PERSONAL_YEAR_V1' && (
          <div className="year-selector">
            <p className="eyebrow">Год разбора</p>
            <div className="context-chips">
              {(yearOptions?.offeredYears ?? (targetYear != null ? [targetYear] : [])).map(year => (
                <button key={year} type="button" className={targetYear === year ? 'selected' : ''} onClick={() => setTargetYear(year)}>
                  {year}
                </button>
              ))}
            </div>
            {!yearOptions && prepare.isPending && <p className="purchase-caption">Подбираю доступные годы...</p>}
          </div>
        )}
      </section>
    )}
    <section className="boundary-card"><ShieldCheck weight="fill" /><div><h3>Границы разбора</h3><p>{product.scope}</p></div></section>
    {softNote && <p className="purchase-caption">{softNote}</p>}
    {notReadyMessage && (
      <section className="product-section compatibility-not-ready" role="status">
        <p>{notReadyMessage}</p>
        <p className="purchase-caption">Можно поправить имя, дату или контекст выше - оплата не списана.</p>
      </section>
    )}
    {alreadyOwned ? (
      <button className="primary-button large" onClick={() => navigate(`/reports/view/${encodeURIComponent(prepare.data!.reportId!)}`)}>
        Открыть разбор <ArrowRight />
      </button>
    ) : product.checkoutEnabled ? (
      telegramBootstrapPending ? (
        <button className="primary-button large" disabled>
          Подтверждаем Telegram...
        </button>
      ) : needsLogin ? (
        <button className="primary-button large telegram-login" disabled={login.isPending} onClick={() => login.mutate()}>
          {login.isPending ? 'Открываем Telegram...' : 'Войти через Telegram и продолжить'}
        </button>
      ) : (
        <button
          className="primary-button large"
          disabled={startCheckout.isPending || Boolean(notReadyMessage) || !contextChoice || (productId === 'PERSONAL_YEAR_V1' && targetYear == null) || (insideTelegramShell && auth.data && !auth.data.hasStableIdentity)}
          onClick={buyReport}
        >
          {insideTelegramShell && auth.data && !auth.data.hasStableIdentity
            ? 'Подтверждаем аккаунт...'
            : startCheckout.isPending
              ? (isCompatibility ? 'Проверяю вашу пару...' : 'Готовим покупку...')
              : `Купить за ${product.price?.formatted}`} <ArrowRight />
        </button>
      )
    ) : (
      <button className="primary-button large" onClick={() => setDisabledModal(true)}>
        {status.purchaseSoon ? 'Скоро откроем покупку' : 'Скоро'}
      </button>
    )}
    <p className="purchase-caption">Разовая покупка. Разбор остаётся у тебя.</p>
    <ErrorNotice error={prepare.error ?? login.error ?? (notReadyMessage ? null : startCheckout.error)} />
    {disabledModal && <ReportCheckoutStub onClose={() => setDisabledModal(false)} />}
  </div>;
}

function asReportContent(value: PremiumReportDetail['content']): PremiumReportContent | null {
  if (!value || typeof value !== 'object') return null;
  return value as PremiumReportContent;
}

function ReportReaderPage() {
  const { reportId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tick, setTick] = useState(0);
  const generationStartedAt = useRef<number | null>(null);
  const detail = useQuery({
    queryKey: ['report-detail', reportId],
    enabled: Boolean(reportId),
    queryFn: () => api.get<PremiumReportDetail>(`/v1/reports/${reportId}`),
    refetchInterval: query => {
      const status = query.state.data?.status;
      return status === 'generating' || status === 'ready_to_generate' || status === 'purchased' || status === 'awaiting_input'
        ? 2000
        : false;
    },
  });

  useEffect(() => {
    const status = detail.data?.status;
    const pollingNow = status === 'generating' || status === 'ready_to_generate' || status === 'purchased' || status === 'awaiting_input';
    if (!pollingNow) {
      generationStartedAt.current = null;
      return;
    }
    if (generationStartedAt.current == null) generationStartedAt.current = Date.now();
    const timer = window.setInterval(() => setTick(value => value + 1), 2000);
    return () => window.clearInterval(timer);
  }, [detail.data?.status]);

  useEffect(() => {
    const status = detail.data?.status;
    if (!status || !reportId) return;
    if (status === 'purchased' || status === 'ready_to_generate' || status === 'awaiting_input') {
      void api.post(`/v1/reports/${reportId}/generate`, {}).then(() => {
        void queryClient.invalidateQueries({ queryKey: ['report-detail', reportId] });
      }).catch(() => undefined);
    }
  }, [detail.data?.status, reportId, queryClient]);

  const reportOpenedTracked = useRef(false);
  useEffect(() => {
    if (reportOpenedTracked.current || detail.data?.status !== 'completed' || detail.data.productId !== 'COMPATIBILITY_V1') return;
    reportOpenedTracked.current = true;
    void trackFunnel('compatibility_report_opened', { productId: 'COMPATIBILITY_V1', source: 'report' });
  }, [detail.data?.status, detail.data?.productId]);

  const retry = useMutation({
    mutationFn: () => api.post<PremiumReportDetail>(`/v1/reports/${reportId}/retry`, {}),
    onSuccess: data => {
      queryClient.setQueryData(['report-detail', reportId], data);
      void queryClient.invalidateQueries({ queryKey: ['my-reports'] });
    },
  });

  if (detail.isPending) return <LoadingCard label="Открываем разбор" />;
  if (detail.error || !detail.data) return <div className="empty-state"><h2>Разбор не найден</h2><ErrorNotice error={detail.error} /><button className="secondary-button" onClick={() => navigate('/reports?tab=mine')}>К моим разборам</button></div>;

  const report = detail.data;
  const polling = report.status === 'generating' || report.status === 'ready_to_generate' || report.status === 'purchased' || report.status === 'awaiting_input';
  if (polling) {
    const phrases = REPORT_LOADING_BY_PRODUCT[report.productId] ?? REPORT_PROGRESS_FALLBACK;
    const progress = report.progressPhase && !/шаблон|Brain|payload|schema|preflight|знан/i.test(report.progressPhase)
      ? report.progressPhase
      : phrases[tick % phrases.length];
    // Progress bar must only advance. Never use tick%N - that resets the bar and looks broken.
    const started = generationStartedAt.current ?? Date.now();
    const elapsedSec = Math.max(0, (Date.now() - started) / 1000);
    const barWidth = Math.min(92, 10 + elapsedSec * 2.2);
    return <div className="generation-page report-generating">
      <BrandMark compact />
      <div className="generation-orbit" aria-hidden="true">
        <span /><span />
        <span className="generation-core"><StarFour weight="fill" /></span>
        <i className="orbit-mark mark-a" /><i className="orbit-mark mark-b" /><i className="orbit-mark mark-c" />
      </div>
      <p className="eyebrow">{report.title}</p>
      <h1>{progress}</h1>
      <p>Покупка сохранена. Собираем большой разбор по твоим числам.</p>
      <div className="generation-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(barWidth)}>
        <span style={{ width: `${barWidth}%` }} />
      </div>
    </div>;
  }

  if (report.status === 'generation_failed' && (report.failureMessage || !report.canRetry)) {
    return <div className="generation-page">
      <BrandMark compact />
      <div className="error-symbol"><MagicWand /></div>
      <h1>{report.title}</h1>
      <p>{report.failureMessage ?? 'Покупка сохранена. Я уже вижу проблему и вернусь с разбором.'}</p>
      {report.canRetry && (
        <button className="primary-button" disabled={retry.isPending} onClick={() => retry.mutate()}>
          {retry.isPending ? 'Запускаю...' : 'Попробовать ещё раз'}
        </button>
      )}
      <ErrorNotice error={retry.error} />
      <button className="text-button" onClick={() => navigate('/reports?tab=mine')}>К моим разборам</button>
    </div>;
  }

  if (report.status === 'generation_failed') {
    return <div className="generation-page">
      <BrandMark compact />
      <div className="error-symbol"><MagicWand /></div>
      <h1>Разбор готовится чуть дольше обычного</h1>
      <p>Покупка сохранена - можно попробовать ещё раз.</p>
      <ErrorNotice error={retry.error} />
      <button className="primary-button" disabled={retry.isPending} onClick={() => retry.mutate()}>
        {retry.isPending ? 'Запускаю...' : 'Попробовать ещё раз'}
      </button>
      <button className="text-button" onClick={() => navigate('/reports?tab=mine')}>К моим разборам</button>
    </div>;
  }

  if (report.status !== 'completed') {
    return <div className="empty-state">
      <h2>{report.title}</h2>
      <p>{reportStatusLabel(report.status)}</p>
      <button className="secondary-button" onClick={() => navigate('/reports?tab=mine')}>К моим разборам</button>
    </div>;
  }

  const content = asReportContent(report.content);
  const sections = content?.sections ?? [];
  const keyNumbers = content?.keyNumbers ?? [];
  const timeline = content?.timeline ?? [];
  const strongDates = content?.strongDates ?? [];
  const months = content?.months ?? [];
  const nextQuestions = (content?.suggestedUserQuestions?.length
    ? content.suggestedUserQuestions
    : content?.nextQuestions) ?? [];
  const consultantQuestion = content?.consultantQuestion ?? null;
  const crossSell = content?.crossSell ?? null;
  const isCompatibility = content?.kind === 'compatibility_report';
  const visibleSections = sections.filter(section => (section.items?.length ?? 0) > 0 || Boolean(section.body?.trim()));

  return <div className="report-reader">
    <button className="back-link" onClick={() => navigate('/reports?tab=mine')}><ArrowLeft /> Мои разборы</button>
    <header className="report-reader-hero">
      <p className="eyebrow">Большой разбор{report.targetYear != null ? ` · ${report.targetYear}` : ''}</p>
      <h1>{content?.title ?? report.title}</h1>
      {content?.subtitle && <p className="report-subtitle">{content.subtitle}</p>}
    </header>
    {content?.summary && <section className="report-section-card summary-card">
      <p className="eyebrow">Кратко</p>
      {content.headings?.summary && <h2>{content.headings.summary}</h2>}
      <Narrative text={content.summary} />
    </section>}
    {keyNumbers.length > 0 && (
      <section className="report-key-numbers">
        <p className="eyebrow">Ключевые числа</p>
        <div>{keyNumbers.map(item => (
          <div key={`${item.label}-${item.value}`}><span className="num-center">{item.value}</span><strong>{item.label}</strong>{item.note && <small>{item.note}</small>}</div>
        ))}</div>
      </section>
    )}
    {visibleSections.map((section, index) => (
      <article key={section.id ?? section.title} className="report-section-card">
        <span className="section-number">0{Math.min(index + 1, 99)}</span>
        <h2>{section.title}</h2>
        {section.anchor && <p className="section-anchor">{section.anchor}</p>}
        {section.items?.length
          ? section.items.map(item => (
            <div key={item.id} className="section-item">
              <h3>{item.heading}</h3>
              <Narrative text={item.body} />
            </div>
          ))
          : <Narrative text={section.body} />}
      </article>
    ))}
    {timeline.length > 0 && (
      <section className="report-timeline">
        <p className="eyebrow">Период</p>
        <h2>Ближайшее движение</h2>
        <div className="timeline-list">{timeline.map(item => (
          <div key={`${item.period}-${item.number ?? ''}`}>
            <span className="num-center">{item.number ?? '·'}</span>
            <div>
              <strong>{item.period}{item.phase ? ` · ${phaseBadgeLabel(item.phase)}` : ''}</strong>
              <Narrative text={item.text} />
            </div>
          </div>
        ))}</div>
      </section>
    )}
    {strongDates.length > 0 && (
      <section className="report-strong-dates">
        <p className="eyebrow">Сильные даты</p>
        <div>{strongDates.map(item => (
          <div key={item.date}>
            <strong>{formatReportDateRu(item.date)}</strong>
            <small>{strongDateCaption(item.role, item.label)}</small>
            <Narrative text={item.text} />
          </div>
        ))}</div>
      </section>
    )}
    {months.length > 0 && (
      <section className="report-month-grid">
        <p className="eyebrow">Месяцы года</p>
        <div>{months.map(item => (
          <article key={item.month}>
            <div className="month-head">
              <strong>{item.title ?? MONTH_NAMES_RU[item.month - 1] ?? `Месяц ${item.month}`}</strong>
              {item.phase && <span className={'phase-badge ' + item.phase}>{phaseBadgeLabel(item.phase)}</span>}
            </div>
            {item.personalMonth != null && <span className="month-number">{item.personalMonth}</span>}
            {item.text && <Narrative text={item.text} />}
          </article>
        ))}</div>
      </section>
    )}
    {content?.finalSynthesis && (
      <section className="report-section-card synthesis-card">
        <p className="eyebrow">Итог</p>
        <h2>{content.headings?.finalSynthesis ?? (isCompatibility ? 'Что я бы вынесла из этого разбора' : 'На что смотреть в первую очередь')}</h2>
        <Narrative text={content.finalSynthesis} />
      </section>
    )}
    {isCompatibility && content?.safetyNote && (
      <section className="report-section-card safety-note-card">
        <p className="eyebrow">Границы разбора</p>
        <h2>{content.headings?.safety ?? 'Что нельзя узнать по двум датам'}</h2>
        <Narrative text={content.safetyNote} />
      </section>
    )}
    {consultantQuestion && (
      <section className="report-section-card">
        <p className="eyebrow">Уточнение</p>
        <h2>Что я бы ещё спросила</h2>
        <p>{consultantQuestion}</p>
      </section>
    )}
    {nextQuestions.length > 0 && (
      <section className="starter-card">
        <p className="eyebrow">Продолжить</p>
        <h2>Можно отправить нумерологу</h2>
        <div>{nextQuestions.map(question => (
          <button key={question} onClick={() => navigate('/numerologist', { state: { question, messageOrigin: 'suggested_question' } })}>{question}<ArrowRight /></button>
        ))}</div>
      </section>
    )}
    {(report.snapshotNote || content) && (
      <p className="snapshot-note">{report.snapshotNote ?? 'Разбор создан по данным профиля на момент покупки.'}</p>
    )}
    {crossSell && (
      <section className="report-cross-sell">
        <p>{crossSell.label}</p>
        {crossSell.availability === 'coming_soon' && crossSell.productId !== 'COMPATIBILITY_V1' ? (
          <span className="soon-chip">Скоро</span>
        ) : crossSell.productId === 'COMPATIBILITY_V1' || crossSell.availability === 'preview_only' ? (
          <Link className="secondary-button" to={`/reports/${encodeURIComponent(crossSell.productId)}`}>Подробнее</Link>
        ) : (
          <Link className="secondary-button" to={`/reports/${encodeURIComponent(crossSell.productId)}`}>Смотреть разбор</Link>
        )}
      </section>
    )}
  </div>;
}

interface NumerologySummary {
  highlights: { lifePath: { value: number }; birthDay: { raw: number }; cycles: { personalYear: number; personalMonth: number; personalDay: number }; matrix22: { nodeCount: number; center: number } };
  source: { engineVersion: string; targetDate: string };
}

function SupportSheet({ onClose, errorReported = false }: { onClose: () => void; errorReported?: boolean }) {
  const queryClient = useQueryClient();
  const [text, setText] = useState('');
  const [sentNotice, setSentNotice] = useState(false);

  const threadQuery = useQuery({
    queryKey: ['support-thread'],
    queryFn: () => api.get<{
      thread: { threadId: string; status: 'open' | 'answered' | 'closed'; updatedAt: string };
      messages: { messageId: string; senderRole: 'user' | 'admin'; body: string; createdAt: string }[];
    }>('/v1/support/thread'),
  });

  const sendMutation = useMutation({
    mutationFn: (body: string) => api.post<{ message: unknown }>('/v1/support/messages', { body }),
    onSuccess: () => {
      setText('');
      setSentNotice(true);
      void queryClient.invalidateQueries({ queryKey: ['support-thread'] });
    },
  });

  const messages = threadQuery.data?.messages ?? [];

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <motion.div className="bottom-sheet support-sheet" role="dialog" aria-modal="true" initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }}>
        <button type="button" className="icon-button sheet-close" onClick={onClose} aria-label="Закрыть"><X /></button>
        <p className="eyebrow">Помощь</p>
        <h2>Техподдержка</h2>
        <p className="support-subline">Есть вопрос или что-то не работает? Напишите нам.</p>

        {errorReported && (
          <div className="notice notice-success" role="status">
            Сигнал об ошибке уже отправлен. При желании опишите, что вы делали перед её появлением.
          </div>
        )}

        {threadQuery.isLoading && <p className="support-subline">Загрузка сообщений...</p>}

        {messages.length > 0 && (
          <div className="support-history">
            {messages.map(msg => (
              <div key={msg.messageId} className={`support-bubble ${msg.senderRole === 'admin' ? 'admin' : 'user'}`}>
                <div className="bubble-header">
                  <strong>{msg.senderRole === 'admin' ? 'Поддержка' : 'Вы'}</strong>
                  <time>{new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' }).format(new Date(msg.createdAt))}</time>
                </div>
                <p className="bubble-body">{msg.body}</p>
              </div>
            ))}
          </div>
        )}

        {sentNotice && (
          <div className="notice notice-success" role="status" style={{ marginBottom: 14 }}>
            Сообщение отправлено. Мы ответим здесь и в Telegram, если это возможно.
          </div>
        )}

        <form onSubmit={e => { e.preventDefault(); if (text.trim()) sendMutation.mutate(text); }}>
          <label className="field-block">
            <span>Ваш вопрос</span>
            <textarea
              value={text}
              onChange={e => { setText(e.target.value); setSentNotice(false); }}
              placeholder="Опишите, что произошло..."
              rows={3}
              maxLength={2000}
            />
          </label>
          <ErrorNotice error={sendMutation.error} showSupportAction={false} />
          <button
            type="submit"
            className="primary-button"
            disabled={!text.trim() || sendMutation.isPending}
          >
            {sendMutation.isPending ? 'Отправляем...' : 'Отправить'} <PaperPlaneRight />
          </button>
        </form>
      </motion.div>
    </div>
  );
}

function SupportPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  return <SupportSheet
    errorReported={searchParams.get('reported') === '1'}
    onClose={() => navigate('/profile', { replace: true })}
  />;
}

function ProfilePage() {
  const navigate = useNavigate(); const queryClient = useQueryClient();
  const preview = useLocation().pathname === '/__preview/profile';
  const profile = useQuery({
    queryKey: ['profile'],
    enabled: !preview,
    initialData: preview ? { user: { userId: 'preview', displayName: 'Валера', birthDate: '1991-07-04', timeZone: 'Europe/Moscow', primaryIntent: 'relationships' as IntentId, activeInterests: ['relationships'] as InterestId[], intentDetail: '', profileRevision: 1 } } : undefined,
    staleTime: PROFILE_STALE_MS,
    queryFn: () => api.get<{ user: User }>('/v1/profile'),
  });
  const numerology = useQuery({
    queryKey: ['numerology'],
    enabled: !preview,
    initialData: preview ? { calculatedProfile: { highlights: { lifePath: { value: 4 }, birthDay: { raw: 4 }, cycles: { personalYear: 3, personalMonth: 3, personalDay: 8 }, matrix22: { nodeCount: 28, center: 8 } }, source: { engineVersion: 'preview', targetDate: '2026-09-24' } } } : undefined,
    staleTime: NUMEROLOGY_STALE_MS,
    retry: 1,
    queryFn: () => api.get<{ calculatedProfile: NumerologySummary; numbersExplainer?: NumbersExplainer }>('/v1/profile/numerology'),
  });
  const user = profile.data?.user;
  const [editing, setEditing] = useState(false); const [deleteStep, setDeleteStep] = useState(0);
  const [explainerOpen, setExplainerOpen] = useState(false);
  const numbersHelpRef = useRef<HTMLButtonElement>(null);
  const [interestSheet, setInterestSheet] = useState(false);
  const [interestDraft, setInterestDraft] = useState<InterestId[]>([]);
  const [name, setName] = useState(''); const [birthDate, setBirthDate] = useState(''); const [timeZone, setTimeZone] = useState('');
  useEffect(() => { if (user) { setName(user.displayName); setBirthDate(user.birthDate); setTimeZone(user.timeZone); } }, [user]);
  const save = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('Профиль не загружен.');
      if (birthDate !== user.birthDate && !window.confirm('Дата рождения изменит расчёты и сделает прошлые AI-результаты неактуальными. Продолжить?')) throw new Error('Изменения отменены.');
      return api.patch<{ user: User }>('/v1/profile', { displayName: name, birthDate, timeZone });
    },
    onSuccess: data => { queryClient.setQueryData(['profile'], data); queryClient.invalidateQueries({ queryKey: ['numerology'] }); queryClient.invalidateQueries({ queryKey: ['daily-forecast'] }); queryClient.invalidateQueries({ queryKey: ['reading-status'] }); setEditing(false); },
  });
  // The daily query is keyed by the interest set, so saving never refetches or regenerates it here.
  const saveInterests = useMutation({
    mutationFn: async (interests: InterestId[]) => preview
      ? { user: { ...user!, activeInterests: interests } }
      : api.put<{ user: User; activeInterests: InterestId[] }>('/v1/profile/active-interests', { interests }),
    onSuccess: data => {
      queryClient.setQueryData(['profile'], { user: data.user });
      setInterestSheet(false);
    },
  });
  const remove = useMutation({
    mutationFn: () => api.delete<{ deleted: boolean }>('/v1/profile', { confirm: true }),
    onSuccess: () => { queryClient.clear(); navigate('/', { replace: true }); },
  });
  const exportData = async () => {
    const response = await fetch('/v1/profile/export', { credentials: 'include' });
    if (!response.ok) throw new Error('Не удалось подготовить экспорт.');
    const blob = await response.blob(); const url = URL.createObjectURL(blob); const anchor = document.createElement('a');
    anchor.href = url; anchor.download = 'numerology-ai-export.json'; anchor.click(); URL.revokeObjectURL(url);
  };

  const mapReady = Boolean(user && numerology.data);
  const waitingFirstLoad = (!user && profile.isPending) || (!numerology.data && numerology.isPending);
  if (!mapReady && waitingFirstLoad) return <LoadingCard label={PROFILE_LOADING} />;
  if (!mapReady) {
    return <div className="center-stage" role="alert">
      <h2>Не удалось открыть карту</h2>
      <p className="purchase-caption">Проверь соединение и попробуй ещё раз. Данные профиля не пропали.</p>
      <ErrorNotice error={profile.error ?? numerology.error} />
      <button
        type="button"
        className="primary-button"
        onClick={() => {
          void profile.refetch();
          void numerology.refetch();
        }}
      >
        Повторить
      </button>
    </div>;
  }
  const numbers = numerology.data!.calculatedProfile.highlights;
  const explainer = explainerMatchesDisplayed(numerology.data!.numbersExplainer, numbers) ? numerology.data!.numbersExplainer! : null;
  const openExplainer = () => {
    setExplainerOpen(true);
    void trackFunnel('numbers_explainer_opened', explainerAnalytics(explainer?.bridge));
    if (!explainer) void numerology.refetch();
  };
  const closeExplainer = () => { setExplainerOpen(false); numbersHelpRef.current?.focus(); };
  const askFromExplainer = (bridge: NumbersExplainer['bridge']) => {
    const state = explainerContinuationState(bridge);
    void trackFunnel('numbers_explainer_primary_question_clicked', explainerAnalytics(bridge));
    rememberPendingContinuation({ question: state.question, sourceSurface: state.sourceSurface, angleType: state.angleType, questionRank: state.questionRank });
    setExplainerOpen(false);
    navigate('/numerologist', { state });
  };
  const activeInterests = userInterests(user);
  const openInterests = () => { setInterestDraft(activeInterests); saveInterests.reset(); setInterestSheet(true); };
  const closeInterests = () => { setInterestSheet(false); setInterestDraft([]); };
  const toggleInterest = (id: InterestId) => {
    setInterestDraft(current => current.includes(id) ? current.filter(item => item !== id) : current.length >= MAX_INTERESTS ? current : [...current, id]);
  };
  const draftUnchanged = interestDraft.length === activeInterests.length && interestDraft.every(id => activeInterests.includes(id));
  const commitInterests = () => { if (draftUnchanged) closeInterests(); else saveInterests.mutate(interestDraft); };
  return <div className="profile-page">
    <header className="profile-hero-header"><div><h1>Профиль</h1><p>Твой путь в цифрах</p></div><button className="profile-edit" onClick={() => setEditing(true)} aria-label="Изменить профиль"><PencilSimple /></button></header>
    <section className="profile-card"><div className="profile-avatar"><span>{user!.displayName.slice(0, 1).toUpperCase()}</span><i><MoonStars weight="fill" /></i></div><div className="profile-identity"><h2>{user!.displayName}</h2><p>{humanDate(user!.birthDate)}</p></div><div className="profile-mantra"><StarFour weight="fill" /><em>Больше,<br />чем числа</em></div><div className="profile-interests"><p className="profile-interests-label">Сферы интереса</p><div className="profile-interest-row">{activeInterests.map(id => <span key={id} className="profile-interest-chip" data-interest={id}><InterestGlyph id={id} />{INTERESTS.find(item => item.id === id)!.title}</span>)}<button type="button" className="profile-interest-add" onClick={openInterests} aria-haspopup="dialog">{activeInterests.length < MAX_INTERESTS ? '+ Добавить' : 'Изменить'}</button></div></div></section>
    <section className="numbers-panel personal-map">
      <div className="section-heading"><div><p className="eyebrow">Мои числа</p><h2>Твоя карта</h2></div><button ref={numbersHelpRef} type="button" className="numbers-help" onClick={openExplainer} aria-haspopup="dialog" aria-expanded={explainerOpen} aria-label="Что означают эти числа? Открыть пояснение"><FileText aria-hidden="true" /><span>Что означают<br />эти числа?</span><CaretRight aria-hidden="true" /></button></div>
      <div className="map-orbit" aria-label="Персональная нумерологическая карта">
        <div className="map-ring ring-outer" aria-hidden="true" />
        <div className="map-ring ring-inner" aria-hidden="true" />
        <div className="map-core">
          <span className="num-center">{numbers.lifePath.value}</span>
          <strong>Число жизненного пути</strong>
        </div>
        <div className="map-sat sat-tl"><Sun weight="fill" /><span className="num-center">{numbers.birthDay.raw}</span><small>День рождения</small></div>
        <div className="map-sat sat-tr"><Compass /><span className="num-center">{numbers.cycles.personalYear}</span><small>Личный год</small></div>
        <div className="map-sat sat-bl"><MoonStars weight="fill" /><span className="num-center">{numbers.cycles.personalMonth}</span><small>Личный месяц</small></div>
        <div className="map-sat sat-br"><StarFour weight="fill" /><span className="num-center">{numbers.cycles.personalDay}</span><small>Личный день</small></div>
        <div className="map-sat sat-bottom"><span className="map-infinity">∞</span><span className="num-center">{numbers.matrix22.center}</span><small>Центр Матрицы 22</small></div>
      </div>
      <p className="numbers-note">Матрица содержит {numbers.matrix22.nodeCount} узлов. 22 - это диапазон архетипов, а не число позиций.</p>
    </section>
    <section className="settings-list">
      <button type="button" onClick={() => navigate('/support')}>
        <span><ChatCircleDots /><span><strong>Техподдержка</strong><small>Есть вопрос или что-то не работает? Напишите нам.</small></span></span>
        <CaretRight />
      </button>
      <button onClick={() => setEditing(true)}><span><UserCircle /><span><strong>Данные профиля</strong><small>Имя, дата, часовой пояс</small></span></span><CaretRight /></button>
      <button onClick={() => void exportData()}><span><DownloadSimple /><span><strong>Экспорт данных</strong><small>Профиль и сохранённые AI-результаты</small></span></span><CaretRight /></button>
      <button className="danger" onClick={() => setDeleteStep(1)}><span><Trash /><span><strong>Удалить профиль</strong><small>Без возможности восстановления</small></span></span><CaretRight /></button>
    </section>
    <PurchasesSection />
    {explainerOpen && <NumbersExplainerSheet explainer={explainer} failed={!numerology.isFetching && (numerology.isError || !explainer)} onRetry={() => void numerology.refetch()} onClose={closeExplainer} onAsk={askFromExplainer} />}
    {interestSheet && createPortal(<div className="modal-backdrop" onClick={event => { if (event.target === event.currentTarget) closeInterests(); }}><motion.div className="bottom-sheet interest-sheet" role="dialog" aria-modal="true" aria-labelledby="interest-sheet-title" initial={{ y: 60 }} animate={{ y: 0 }}>
      <button type="button" className="icon-button sheet-close" onClick={closeInterests} aria-label="Закрыть"><X /></button>
      <p className="eyebrow">Сферы интереса</p>
      <h2 id="interest-sheet-title">О чём прогноз дня</h2>
      <p className="interest-sheet-note">{interestDraft.length >= MAX_INTERESTS ? 'Выбрано 3 из 3. Чтобы заменить сферу, сначала сними одну.' : `Выбери от 1 до ${MAX_INTERESTS} сфер - по каждой будет отдельный абзац в прогнозе.`}</p>
      <div className="interest-options">{INTERESTS.map(item => {
        const selected = interestDraft.includes(item.id);
        const locked = !selected && interestDraft.length >= MAX_INTERESTS;
        return <button type="button" key={item.id} className={'interest-option' + (selected ? ' selected' : '') + (locked ? ' locked' : '')} aria-pressed={selected} aria-disabled={locked} onClick={() => toggleInterest(item.id)}>
          <span className="interest-option-icon" aria-hidden="true"><InterestGlyph id={item.id} /></span>
          <span className="interest-option-copy"><strong>{item.title}</strong><small>{item.hint}</small></span>
          <span className="interest-option-check" aria-hidden="true">{selected && <Check weight="bold" />}</span>
        </button>;
      })}</div>
      <ErrorNotice error={saveInterests.error} />
      <div className="interest-sheet-actions">
        <button type="button" className="text-button" onClick={closeInterests}>Отмена</button>
        <button type="button" className="primary-button" disabled={interestDraft.length < 1 || saveInterests.isPending} onClick={commitInterests}>{saveInterests.isPending ? 'Сохраняем...' : 'Сохранить'}</button>
      </div>
    </motion.div></div>, document.body)}
    {editing && <div className="modal-backdrop"><motion.form className="bottom-sheet form-sheet" onSubmit={event => { event.preventDefault(); save.mutate(); }} initial={{ y: 60 }} animate={{ y: 0 }}>
      <button type="button" className="icon-button sheet-close" onClick={() => setEditing(false)} aria-label="Закрыть"><X /></button><p className="eyebrow">Настройки</p><h2>Данные профиля</h2>
      <label className="field-block"><span>Имя</span><input value={name} onChange={event => setName(event.target.value)} /></label><label className="field-block"><span>Дата рождения</span><input type="date" value={birthDate} onChange={event => setBirthDate(event.target.value)} /></label><label className="field-block"><span>Часовой пояс</span><select value={timeZone} onChange={event => setTimeZone(event.target.value)}><option value={timeZone}>{timeZone}</option><option value="Europe/Moscow">Europe/Moscow</option><option value="Asia/Almaty">Asia/Almaty</option><option value="Asia/Ho_Chi_Minh">Asia/Ho_Chi_Minh</option><option value="Europe/Berlin">Europe/Berlin</option></select></label>
      <ErrorNotice error={save.error} /><button className="primary-button" disabled={save.isPending}>{save.isPending ? 'Сохраняем...' : 'Сохранить'}</button>
    </motion.form></div>}
    {deleteStep > 0 && <div className="modal-backdrop"><motion.div className="bottom-sheet danger-sheet" initial={{ y: 60 }} animate={{ y: 0 }}><div className="danger-symbol"><Trash /></div><h2>{deleteStep === 1 ? 'Удалить профиль?' : 'Подтверди ещё раз'}</h2><p>{deleteStep === 1 ? 'Будут удалены профиль, история, разбор и прогнозы.' : 'Это действие необратимо. Мы не сможем восстановить данные.'}</p><ErrorNotice error={remove.error} /><button className="danger-button" onClick={() => deleteStep === 1 ? setDeleteStep(2) : remove.mutate()} disabled={remove.isPending}>{remove.isPending ? 'Удаляем...' : deleteStep === 1 ? 'Продолжить удаление' : 'Удалить навсегда'}</button><button className="text-button" onClick={() => setDeleteStep(0)}>Отмена</button></motion.div></div>}
  </div>;
}

function ProtectedShell({ children }: { children: ReactNode }) {
  // Shell + bottom nav render immediately; page content waits on profile.
  return <AppShell><Protected>{children}</Protected></AppShell>;
}

function useTelegramAuthStatus(): TelegramAuthStatus {
  const [status, setStatus] = useState<TelegramAuthStatus>(() => getTelegramAuthStatus());
  useEffect(() => subscribeTelegramAuthStatus(setStatus), []);
  return status;
}

function useTelegramMiniAppIdentity() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const navigateRef = useRef(navigate);
  const pathnameRef = useRef(location.pathname);
  navigateRef.current = navigate;
  pathnameRef.current = location.pathname;

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!looksLikeTelegramMiniAppShell() && !isTelegramMiniApp()) {
        return;
      }

      // App-scoped bootstrap: one initData auth per JS session (deduped inside authenticateTelegramMiniApp).
      // Must NOT re-run on tab/path changes - that remounted Protected and raced Profile fetches.
      const outcome = await authenticateTelegramMiniApp({
        post: (path, body) => api.post(path, body),
        createSession: async () => {
          await api.post(
            '/v1/onboarding/sessions',
            { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone },
            idempotencyKey('tg-session'),
          );
        },
      });
      if (cancelled) return;

      void queryClient.invalidateQueries({ queryKey: ['auth-me'] });
      void queryClient.invalidateQueries({ queryKey: ['profile'] });
      void queryClient.invalidateQueries({ queryKey: ['onboarding'] });
      // Calendar/forecast can 401 during Mini App auth race; force recovery after identity settles.
      void queryClient.invalidateQueries({ queryKey: ['calendar'] });
      void queryClient.invalidateQueries({ queryKey: ['daily-forecast'] });
      void queryClient.prefetchQuery({
        queryKey: ['catalog'],
        staleTime: 300_000,
        queryFn: () => api.get<{ products: Product[] }>('/v1/catalog'),
      });
      void queryClient.prefetchQuery({
        queryKey: ['numerology'],
        staleTime: NUMEROLOGY_STALE_MS,
        queryFn: () => api.get<{ calculatedProfile: NumerologySummary }>('/v1/profile/numerology'),
      });
      void queryClient.prefetchQuery({
        queryKey: ['my-reports'],
        staleTime: REPORTS_LIST_STALE_MS,
        queryFn: () => api.get<{ reports: PremiumReportListItem[] }>('/v1/reports'),
      });

      const lifecycleToken = new URLSearchParams(window.location.search).get('lc');
      if (lifecycleToken && /^[A-Za-z0-9_-]{8,64}$/.test(lifecycleToken)) {
        const opened = await api.post<{
          path: string; campaign: string; step: number; variantId: string; productId: string | null; primaryQuestion: string | null;
        }>('/v1/lifecycle/open', { token: lifecycleToken }).catch(() => null);
        if (opened) {
          rememberLifecycleAttribution({
            sourceCampaign: opened.campaign, campaignStep: opened.step, variantId: opened.variantId, productId: opened.productId,
          });
          if (opened.primaryQuestion) {
            rememberPendingContinuation({ question: opened.primaryQuestion, sourceSurface: 'free_question', questionRank: 'primary' });
          }
          if (opened.path) {
            navigateRef.current(opened.path, { replace: true });
            if (outcome === 'authenticated') void trackFunnel('mini_app_open');
            return;
          }
        }
      }

      if (outcome === 'authenticated') {
        void trackFunnel('mini_app_open');
        const startParam = getTelegramStartParam();
        if (startParam?.startsWith('pay_') && startParam.length > 4) {
          const paidOrderId = startParam.slice(4);
          if (/^[0-9a-f-]{36}$/i.test(paidOrderId)) {
            navigateRef.current(`/payment/return?orderId=${encodeURIComponent(paidOrderId)}`, { replace: true });
            return;
          }
        }
        const onboarding = await api.get<{ onboarding: Onboarding }>('/v1/onboarding').catch(() => null);
        if (onboarding?.onboarding.status === 'complete') {
          const path = pathnameRef.current;
          if (path === '/' || path === '/onboarding' || path === '/reading') {
            navigateRef.current('/today', { replace: true });
          }
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // Intentionally app-mount scoped: tab navigation must not restart Telegram auth.
  }, [queryClient]);
}

export function App() {
  useTelegramMiniAppIdentity();
  return <Routes>
    <Route path="/" element={<WelcomePage />} />
    <Route path="/onboarding" element={<OnboardingPage />} />
    <Route path="/reading" element={<Protected><ReadingPage /></Protected>} />
    {import.meta.env.DEV && <Route path="/__preview/today" element={<AppShell><TodayPreviewPage /></AppShell>} />}
    {import.meta.env.DEV && <Route path="/__preview/continuation" element={<AppShell><ContinuationPreviewPage /></AppShell>} />}
    {import.meta.env.DEV && <Route path="/__preview/reports" element={<AppShell><ReportsPage /></AppShell>} />}
    {import.meta.env.DEV && <Route path="/__preview/profile" element={<AppShell><ProfilePage /></AppShell>} />}
    <Route path="/today" element={<ProtectedShell><TodayPage /></ProtectedShell>} />
    <Route path="/numerologist" element={<ProtectedShell><NumerologistPage /></ProtectedShell>} />
    <Route path="/reports" element={<ProtectedShell><ReportsPage /></ProtectedShell>} />
    <Route path="/reports/view/:reportId" element={<ProtectedShell><ReportReaderPage /></ProtectedShell>} />
    <Route path="/reports/generating/:reportId" element={<ProtectedShell><ReportReaderPage /></ProtectedShell>} />
    <Route path="/reports/:productId" element={<ProtectedShell><ReportProductPage /></ProtectedShell>} />
    <Route path="/checkout" element={<ProtectedShell><CheckoutPage /></ProtectedShell>} />
    <Route path="/payment/return" element={<PaymentReturnPage />} />
    <Route path="/auth/conflict" element={<AuthConflictPage />} />
    <Route path="/support" element={<SupportPage />} />
    <Route path="/profile" element={<ProtectedShell><ProfilePage /></ProtectedShell>} />
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>;
}
